import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { ArrowLeft, Shield, Tag, Users, Trash2, Plus, CheckCircle2, XCircle, Clock, Percent, Calendar, TrendingUp, Smartphone, Printer, Download, Loader2, Megaphone, Mail, Send, Eye, Trophy, ExternalLink, ChevronDown, ChevronUp, Save, Sparkles, Crown, FileText, Quote, Settings, MessageSquare, DollarSign, RefreshCw, CreditCard, AlertTriangle } from 'lucide-react';
import { cn } from '../lib/utils';

const TYPE_LABELS = {
  first_month: { label: 'First Month %', hint: 'e.g., 50% off $7.99 first month' },
  first_year: { label: 'First Year %', hint: 'e.g., 50% off $59 first year' },
  lifetime: { label: 'Lifetime %', hint: 'Every renewal forever' },
  extended_trial: { label: 'Extended Trial', hint: 'e.g., 30-day trial instead of 7' },
};

const INQUIRY_STATUSES = [
  { value: 'new',        label: 'New',        pill: 'bg-amber-100 text-amber-800 border-amber-300' },
  { value: 'responding', label: 'Responding', pill: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'pilot',      label: 'Pilot',      pill: 'bg-violet-100 text-violet-800 border-violet-300' },
  { value: 'partner',    label: 'Partner',    pill: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  { value: 'declined',   label: 'Declined',   pill: 'bg-stone-200 text-stone-600 border-stone-300' },
];
const statusMeta = (s) => INQUIRY_STATUSES.find(x => x.value === (s || 'new')) || INQUIRY_STATUSES[0];

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, token } = useAuthStore();

  const [codes, setCodes] = useState([]);
  const [grants, setGrants] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  const [newCode, setNewCode] = useState({ code: '', type: 'first_month', percent_off: 50, trial_days: 30, active: true, max_uses: '', expires_at: '', notes: '' });

  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [newGrant, setNewGrant] = useState({ phone_number: '', expires_at: '', note: '' });

  // Marketing tab — flyer generator
  const [flyerLeague, setFlyerLeague] = useState('');
  const [flyerPromo, setFlyerPromo] = useState('');
  const [flyerDownloading, setFlyerDownloading] = useState(false);

  // Welcome-email test harness
  const [emailStage, setEmailStage] = useState('day0');
  const [emailTo, setEmailTo] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [emailPreviewSubject, setEmailPreviewSubject] = useState('');
  const [dripRunning, setDripRunning] = useState(false);
  const [dripResult, setDripResult] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [remindersRunning, setRemindersRunning] = useState(false);
  const [remindersResult, setRemindersResult] = useState(null);

  const previewWelcomeEmail = async (alsoSend = false) => {
    setEmailSending(true);
    try {
      const payload = { stage: emailStage, display_name: 'TestCoach' };
      if (alsoSend && emailTo.trim()) payload.to = emailTo.trim();
      const r = await api.post('/admin/welcome-email/preview', payload);
      setEmailPreviewHtml(r.data.html);
      setEmailPreviewSubject(r.data.subject);
      if (alsoSend) {
        if (r.data.sent) toast.success(`Test email sent to ${emailTo}!`);
        else toast.error('Could not send - check RESEND_API_KEY on backend.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setEmailSending(false);
    }
  };

  const runDripNow = async () => {
    setDripRunning(true);
    try {
      const r = await api.post('/admin/welcome-email/run-drip');
      setDripResult(r.data);
      toast.success(`Drip swept: Day 1 sent ${r.data.day1}, Day 3 sent ${r.data.day3}`);
      loadDeliveries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Drip run failed');
    } finally {
      setDripRunning(false);
    }
  };

  const runGameRemindersNow = async () => {
    setRemindersRunning(true);
    try {
      const r = await api.post('/admin/welcome-email/run-game-reminders');
      setRemindersResult(r.data);
      toast.success(`Reminders swept: ${r.data.sent} sent across ${r.data.games_checked} games`);
      loadDeliveries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reminder run failed');
    } finally {
      setRemindersRunning(false);
    }
  };

  // AI Lineup feature announcement (one-time blast, idempotent on backend)
  const [aiBlastLoading, setAiBlastLoading] = useState(false);
  const [aiBlastResult, setAiBlastResult] = useState(null);

  // Blog post announcement blast (generic — pick any published article by slug)
  const [blogBlastSlug, setBlogBlastSlug] = useState('5-things-every-little-league-coach-should-track');
  const [blogBlastPromo, setBlogBlastPromo] = useState('TRIAL30');
  const [blogBlastLoading, setBlogBlastLoading] = useState(false);
  const [blogBlastResult, setBlogBlastResult] = useState(null);
  const [blogBlastTo, setBlogBlastTo] = useState('');
  const [blogBlastPreviewHtml, setBlogBlastPreviewHtml] = useState('');
  const [blogBlastPreviewSubject, setBlogBlastPreviewSubject] = useState('');

  const previewBlogBlast = async (alsoSend = false) => {
    if (!blogBlastSlug.trim()) { toast.error('Enter a published article slug'); return; }
    setBlogBlastLoading(true);
    try {
      const payload = {};
      if (alsoSend && blogBlastTo.trim()) payload.to = blogBlastTo.trim();
      if (blogBlastPromo.trim()) payload.promo = blogBlastPromo.trim().toUpperCase();
      const r = await api.post(`/admin/blog-announcement/${encodeURIComponent(blogBlastSlug.trim())}/preview`, payload);
      setBlogBlastPreviewHtml(r.data.html);
      setBlogBlastPreviewSubject(r.data.subject);
      if (alsoSend) {
        if (r.data.sent) toast.success(`Test email sent to ${blogBlastTo}!`);
        else toast.error('Could not send — check RESEND_API_KEY on backend.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setBlogBlastLoading(false);
    }
  };

  const previewBlogBlastCount = async () => {
    if (!blogBlastSlug.trim()) { toast.error('Enter a published article slug'); return; }
    setBlogBlastLoading(true);
    try {
      const params = new URLSearchParams({ dry_run: 'true' });
      if (blogBlastPromo.trim()) params.set('promo', blogBlastPromo.trim().toUpperCase());
      const r = await api.post(`/admin/blog-announcement/${encodeURIComponent(blogBlastSlug.trim())}?${params.toString()}`);
      setBlogBlastResult({ ...r.data, sent_now: null });
      const promoNote = r.data.promo_code ? ` · auto-applying ${r.data.promo_code}` : '';
      toast.success(`Would send to ${r.data.would_send_to} coaches (${r.data.already_sent} already received it)${promoNote}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setBlogBlastLoading(false);
    }
  };

  const sendBlogBlast = async () => {
    if (!blogBlastSlug.trim()) { toast.error('Enter a published article slug'); return; }
    const promo = blogBlastPromo.trim().toUpperCase();
    const promoLine = promo ? `\n\nPromo "${promo}" will be auto-applied via the article URL — readers who scan get it for free.` : '';
    if (!confirm(`Send the blog announcement for "${blogBlastSlug}" to every coach who hasn't received it yet?${promoLine}\n\nIdempotent — only NEW coaches will be emailed on subsequent clicks.`)) return;
    setBlogBlastLoading(true);
    try {
      const params = new URLSearchParams();
      if (promo) params.set('promo', promo);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const r = await api.post(`/admin/blog-announcement/${encodeURIComponent(blogBlastSlug.trim())}${qs}`);
      setBlogBlastResult(r.data);
      toast.success(`Sent to ${r.data.sent_now} coaches · ${r.data.failed_now} failed`);
      loadDeliveries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Blast failed');
    } finally {
      setBlogBlastLoading(false);
    }
  };

  // Users tab — full registry of coaches + parents + admins
  const [usersData, setUsersData] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all'); // 'all' | 'coach' | 'parent' | 'admin'
  const [signupsSeries, setSignupsSeries] = useState(null);
  const [actionMenuFor, setActionMenuFor] = useState(null); // user id with open menu
  const [messageDialog, setMessageDialog] = useState(null); // { user, subject, body }

  const loadSignupsSparkline = async () => {
    try {
      const r = await api.get('/admin/users/signups-daily?days=14');
      setSignupsSeries(r.data);
    } catch (err) {
      // Silent — non-critical
    }
  };
  const [streamAnalytics, setStreamAnalytics] = useState(null);
  const [sourcesBreakdown, setSourcesBreakdown] = useState(null);
  const [liveActivity, setLiveActivity] = useState(null);
  const loadStreamAnalytics = async () => {
    try {
      const r = await api.get('/admin/stream-analytics');
      setStreamAnalytics(r.data);
    } catch (err) {
      // Silent
    }
  };
  const loadSourcesBreakdown = async () => {
    try {
      const r = await api.get('/admin/users/sources-breakdown');
      setSourcesBreakdown(r.data);
    } catch (err) {
      // Silent
    }
  };
  const loadLiveActivity = async () => {
    try {
      const r = await api.get('/admin/live-activity');
      setLiveActivity(r.data);
    } catch (err) {
      // Silent
    }
  };
  const loadUsers = async (overrides = {}) => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams();
      const q = overrides.q !== undefined ? overrides.q : userSearch;
      const role = overrides.role !== undefined ? overrides.role : userRoleFilter;
      if (q && q.trim()) params.set('q', q.trim());
      if (role && role !== 'all') params.set('role', role);
      params.set('limit', '500');
      const r = await api.get(`/admin/users?${params.toString()}`);
      setUsersData(r.data);
      // Refresh sparkline + stream analytics + source breakdown + live activity alongside
      loadSignupsSparkline();
      loadStreamAnalytics();
      loadSourcesBreakdown();
      loadLiveActivity();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };
  const grantPremium = async (user, days = 30) => {
    if (!confirm(`Grant ${days} days of premium to ${user.username}?`)) return;
    try {
      await api.post(`/admin/users/${user.id}/grant-premium`, { days });
      toast.success(`Granted ${days} days of premium to ${user.username}`);
      setActionMenuFor(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant premium');
    }
  };
  const revokePremium = async (user) => {
    if (!confirm(`Revoke premium from ${user.username}?`)) return;
    try {
      await api.post(`/admin/users/${user.id}/revoke-premium`);
      toast.success(`Premium revoked from ${user.username}`);
      setActionMenuFor(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke premium');
    }
  };
  const promoteToAdmin = async (user) => {
    if (!confirm(`Promote ${user.username} to admin?`)) return;
    try {
      await api.post('/admin/promote', { phone_number: user.phone_number });
      toast.success(`${user.username} promoted to admin`);
      setActionMenuFor(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to promote');
    }
  };
  const deleteUser = async (user) => {
    if (!confirm(`Delete ${user.username}? This cannot be undone.`)) return;
    if (!confirm(`Last warning — really delete ${user.username}?`)) return;
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success(`${user.username} deleted`);
      setActionMenuFor(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };
  const openMessageDialog = (user) => {
    setActionMenuFor(null);
    if (!user.email) {
      toast.error('This user has no email on file');
      return;
    }
    setMessageDialog({ user, subject: '', body: '' });
  };
  const sendMessage = async () => {
    if (!messageDialog?.user || !messageDialog.subject.trim() || !messageDialog.body.trim()) {
      toast.error('Subject and message required');
      return;
    }
    try {
      await api.post(`/admin/users/${messageDialog.user.id}/send-message`, {
        subject: messageDialog.subject,
        message: messageDialog.body,
      });
      toast.success(`Email sent to ${messageDialog.user.username}`);
      setMessageDialog(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    }
  };
  const exportUsersCsv = () => {
    if (!usersData?.rows?.length) {
      toast.error('No users to export');
      return;
    }
    const cols = ['username', 'email', 'phone_number', 'registered_as', 'is_admin',
                  'subscription_tier', 'subscription_active', 'subscription_expires_at',
                  'trial_started_at', 'created_at'];
    const escapeCsv = (v) => {
      if (v == null) return '';
      const s = String(v);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };
    const header = cols.join(',');
    const rows = usersData.rows.map(u => cols.map(c => escapeCsv(u[c])).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diamond-scorer-users-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${usersData.rows.length} users`);
  };
  const previewAiBlast = async () => {
    setAiBlastLoading(true);
    try {
      const r = await api.post('/admin/feature-announcement/ai-lineup?dry_run=true');
      setAiBlastResult({ ...r.data, sent_now: null });
      toast.success(`Would send to ${r.data.would_send_to} coaches (${r.data.already_sent} already received it).`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setAiBlastLoading(false);
    }
  };
  const sendAiBlast = async () => {
    if (!confirm("Send the AI Lineup announcement email to every coach who hasn't received it yet?\n\nThis is idempotent — if you click again later, only NEW coaches will be emailed.")) return;
    setAiBlastLoading(true);
    try {
      const r = await api.post('/admin/feature-announcement/ai-lineup');
      setAiBlastResult(r.data);
      toast.success(`Sent to ${r.data.sent_now} coaches · ${r.data.failed_now} failed`);
      loadDeliveries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Blast failed');
    } finally {
      setAiBlastLoading(false);
    }
  };

  const loadDeliveries = async () => {
    try {
      const r = await api.get('/admin/welcome-email/deliveries');
      setDeliveries(r.data || []);
    } catch {}
  };

  // League partner inquiries pipeline
  const [inquiries, setInquiries] = useState([]);
  const [inquiryFilter, setInquiryFilter] = useState('all'); // all | new | responding | pilot | partner | declined
  const [expandedInquiry, setExpandedInquiry] = useState(null);
  const [inquiryNotesDraft, setInquiryNotesDraft] = useState({});

  // Subscriptions dashboard
  const [subsData, setSubsData] = useState(null);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsSyncing, setSubsSyncing] = useState(false);

  const loadSubsDashboard = async () => {
    setSubsLoading(true);
    try {
      const r = await api.get('/admin/subscriptions/dashboard');
      setSubsData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load subscriptions');
    } finally {
      setSubsLoading(false);
    }
  };

  const syncStripeSubs = async () => {
    setSubsSyncing(true);
    try {
      const r = await api.post('/admin/subscriptions/sync');
      toast.success(`Synced from Stripe · ${r.data.synced} updated · ${r.data.skipped} skipped`);
      loadSubsDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Stripe sync failed');
    } finally {
      setSubsSyncing(false);
    }
  };

  // Format cents → "$x.xx"
  const fmtCents = (c) => {
    if (c == null) return '—';
    const d = (c / 100).toFixed(2);
    return `$${d}`;
  };
  const fmtCentsRound = (c) => {
    if (c == null) return '—';
    return `$${Math.round(c / 100)}`;
  };

  // ----- CMS Tab state -----
  const [cmsSubTab, setCmsSubTab] = useState('settings'); // settings | articles | testimonials | contacts
  const [siteSettings, setSiteSettings] = useState({
    support_email: '', support_phone: '', business_address: '',
    twitter_url: '', facebook_url: '', instagram_url: '', youtube_url: '',
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [articles, setArticles] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [contactMsgs, setContactMsgs] = useState([]);
  const [articleDialog, setArticleDialog] = useState(null); // null | { ...article }
  const [testimonialDialog, setTestimonialDialog] = useState(null); // null | { ...t }

  const loadCmsAll = async () => {
    try {
      const [s, a, t, c] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-settings/public`).then(r => r.json()),
        api.get('/admin/articles'),
        api.get('/admin/testimonials'),
        api.get('/admin/contact-submissions'),
      ]);
      setSiteSettings({
        support_email: s.support_email || '',
        support_phone: s.support_phone || '',
        business_address: s.business_address || '',
        twitter_url: s.twitter_url || '',
        facebook_url: s.facebook_url || '',
        instagram_url: s.instagram_url || '',
        youtube_url: s.youtube_url || '',
      });
      setArticles(a.data || []);
      setTestimonials(t.data || []);
      setContactMsgs(c.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load CMS data');
    }
  };

  const saveSiteSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/admin/site-settings', siteSettings);
      toast.success('Site settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSavingSettings(false);
    }
  };

  const saveArticle = async () => {
    if (!articleDialog) return;
    const payload = {
      title: articleDialog.title,
      slug: articleDialog.slug || '',
      cover_image_url: articleDialog.cover_image_url || '',
      excerpt: articleDialog.excerpt || '',
      body_markdown: articleDialog.body_markdown || '',
      published: !!articleDialog.published,
      author: articleDialog.author || 'Diamond Scorer',
    };
    if (!payload.title.trim()) { toast.error('Title is required'); return; }
    try {
      if (articleDialog.id) {
        await api.put(`/admin/articles/${articleDialog.id}`, payload);
        toast.success('Article updated');
      } else {
        await api.post('/admin/articles', payload);
        toast.success('Article created');
      }
      setArticleDialog(null);
      loadCmsAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const deleteArticle = async (a) => {
    if (!window.confirm(`Delete article "${a.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/articles/${a.id}`);
      toast.success('Deleted');
      loadCmsAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const togglePublishArticle = async (a) => {
    try {
      await api.put(`/admin/articles/${a.id}`, { published: !a.published });
      loadCmsAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const saveTestimonial = async () => {
    if (!testimonialDialog) return;
    const payload = {
      name: testimonialDialog.name || '',
      role: testimonialDialog.role || '',
      quote: testimonialDialog.quote || '',
      rating: parseInt(testimonialDialog.rating || 5, 10),
      photo_url: testimonialDialog.photo_url || '',
      published: !!testimonialDialog.published,
      sort_order: parseInt(testimonialDialog.sort_order || 0, 10),
    };
    if (!payload.name.trim() || !payload.quote.trim()) {
      toast.error('Name and quote are required');
      return;
    }
    try {
      if (testimonialDialog.id) {
        await api.put(`/admin/testimonials/${testimonialDialog.id}`, payload);
        toast.success('Testimonial updated');
      } else {
        await api.post('/admin/testimonials', payload);
        toast.success('Testimonial created');
      }
      setTestimonialDialog(null);
      loadCmsAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const deleteTestimonial = async (t) => {
    if (!window.confirm(`Delete testimonial from ${t.name}?`)) return;
    try {
      await api.delete(`/admin/testimonials/${t.id}`);
      toast.success('Deleted');
      loadCmsAll();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const togglePublishTestimonial = async (t) => {
    try {
      await api.put(`/admin/testimonials/${t.id}`, { published: !t.published });
      loadCmsAll();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const deleteContactMsg = async (m) => {
    if (!window.confirm(`Delete message from ${m.name}?`)) return;
    try {
      await api.delete(`/admin/contact-submissions/${m.id}`);
      toast.success('Deleted');
      loadCmsAll();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const loadInquiries = async () => {
    try {
      const r = await api.get('/admin/league-partner/inquiries');
      setInquiries(r.data || []);
    } catch {}
  };

  const updateInquiry = async (id, patch) => {
    try {
      const r = await api.patch(`/admin/league-partner/inquiries/${id}`, patch);
      setInquiries(prev => prev.map(x => x.id === id ? r.data : x));
      if (patch.status) toast.success(`Status updated to "${patch.status}"`);
      if (patch.notes !== undefined) toast.success('Notes saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Delete this inquiry permanently?')) return;
    try {
      await api.delete(`/admin/league-partner/inquiries/${id}`);
      setInquiries(prev => prev.filter(x => x.id !== id));
      toast.success('Inquiry deleted');
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const downloadMarketingFlyer = async () => {
    setFlyerDownloading(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams();
      if (flyerLeague.trim()) params.set('league', flyerLeague.trim());
      if (flyerPromo.trim()) params.set('promo', flyerPromo.trim().toUpperCase());
      const qs = params.toString() ? `?${params.toString()}` : '';
      const resp = await fetch(`${base}/api/marketing/flyer.pdf${qs}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const slug = flyerLeague.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'general';
      a.download = `diamond-scorer-flyer-${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const parts = [];
      if (flyerLeague.trim()) parts.push(`for "${flyerLeague.trim()}"`);
      if (flyerPromo.trim()) parts.push(`with code ${flyerPromo.trim().toUpperCase()}`);
      toast.success(`Flyer ${parts.join(' ')} downloaded!`.replace('  ', ' ').trim() || 'Flyer downloaded!');
    } catch {
      toast.error('Could not generate flyer');
    } finally {
      setFlyerDownloading(false);
    }
  };

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/home');
      return;
    }
    loadAll();
  }, [token, user?.is_admin]);

  const loadAll = async () => {
    try {
      const [c, g, a, i] = await Promise.all([
        api.get('/admin/discount-codes'),
        api.get('/admin/premium-grants'),
        api.get('/admin/install-analytics'),
        api.get('/admin/league-partner/inquiries'),
      ]);
      setCodes(c.data || []);
      setGrants(g.data || []);
      setAnalytics(a.data || null);
      setInquiries(i.data || []);
      // Load sparkline alongside other admin data so it's ready when user clicks the Users tab
      loadSignupsSparkline();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async () => {
    const payload = {
      code: newCode.code.trim().toUpperCase(),
      type: newCode.type,
      active: newCode.active,
      notes: newCode.notes || null,
      max_uses: newCode.max_uses ? parseInt(newCode.max_uses, 10) : null,
      expires_at: newCode.expires_at || null,
    };
    if (newCode.type === 'extended_trial') {
      payload.trial_days = parseInt(newCode.trial_days, 10);
    } else {
      payload.percent_off = parseInt(newCode.percent_off, 10);
    }
    try {
      await api.post('/admin/discount-codes', payload);
      toast.success('Code created');
      setCodeDialogOpen(false);
      setNewCode({ code: '', type: 'first_month', percent_off: 50, trial_days: 30, active: true, max_uses: '', expires_at: '', notes: '' });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create code');
    }
  };

  const toggleCodeActive = async (code) => {
    try {
      await api.patch(`/admin/discount-codes/${code.id}`, { active: !code.active });
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteCode = async (code) => {
    if (!window.confirm(`Delete code ${code.code}? Existing uses won't be refunded.`)) return;
    try {
      await api.delete(`/admin/discount-codes/${code.id}`);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const submitGrant = async () => {
    const digits = newGrant.phone_number.replace(/\D/g, '');
    if (!digits) { toast.error('Phone number required'); return; }
    try {
      await api.post('/admin/premium-grants', {
        phone_number: digits,
        note: newGrant.note || null,
        expires_at: newGrant.expires_at || null,
      });
      toast.success('Premium access granted');
      setGrantDialogOpen(false);
      setNewGrant({ phone_number: '', expires_at: '', note: '' });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create grant');
    }
  };

  const revokeGrant = async (grant) => {
    if (!window.confirm(`Revoke premium access for ${grant.phone_number}?`)) return;
    try {
      await api.delete(`/admin/premium-grants/${grant.id}`);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center text-stone-600">
        Loading admin panel…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 p-4 md:p-6" data-testid="admin-page">
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/home')} className="text-stone-700" data-testid="admin-back">
            <ArrowLeft size={16} className="mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2 text-stone-700">
            <Shield size={18} className="text-amber-600" />
            <span className="font-bold text-lg">Admin Panel</span>
          </div>
          <div className="w-16" />
        </div>

        <Tabs defaultValue="codes" className="space-y-4">
          <TabsList className="w-full bg-stone-200">
            <TabsTrigger value="codes" className="flex-1" data-testid="admin-tab-codes">
              <Tag size={14} className="mr-1" /> Discount Codes
            </TabsTrigger>
            <TabsTrigger value="grants" className="flex-1" data-testid="admin-tab-grants">
              <Users size={14} className="mr-1" /> Premium Grants
            </TabsTrigger>
            <TabsTrigger value="marketing" className="flex-1" data-testid="admin-tab-marketing">
              <Megaphone size={14} className="mr-1" /> Marketing
            </TabsTrigger>
            <TabsTrigger value="emails" className="flex-1" data-testid="admin-tab-emails" onClick={loadDeliveries}>
              <Mail size={14} className="mr-1" /> Coach Emails
            </TabsTrigger>
            <TabsTrigger value="leagues" className="flex-1" data-testid="admin-tab-leagues" onClick={loadInquiries}>
              <Trophy size={14} className="mr-1" /> League Inquiries
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex-1" data-testid="admin-tab-analytics">
              <TrendingUp size={14} className="mr-1" /> Install Funnel
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="flex-1" data-testid="admin-tab-subscriptions" onClick={loadSubsDashboard}>
              <DollarSign size={14} className="mr-1" /> Subscriptions
            </TabsTrigger>
            <TabsTrigger value="users" className="flex-1" data-testid="admin-tab-users" onClick={() => loadUsers()}>
              <Users size={14} className="mr-1" /> Users
            </TabsTrigger>
            <TabsTrigger value="cms" className="flex-1" data-testid="admin-tab-cms" onClick={loadCmsAll}>
              <FileText size={14} className="mr-1" /> Site / CMS
            </TabsTrigger>
          </TabsList>

          {/* Discount Codes */}
          <TabsContent value="codes">
            <Card>
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-base">Promo & Discount Codes</CardTitle>
                <Button size="sm" onClick={() => setCodeDialogOpen(true)} className="bg-amber-600 hover:bg-amber-500 text-white" data-testid="new-code-btn">
                  <Plus size={14} className="mr-1" /> New Code
                </Button>
              </CardHeader>
              <CardContent className="space-y-2">
                {codes.length === 0 ? (
                  <p className="text-stone-500 text-sm text-center py-6">No codes yet. Create one to start a promo.</p>
                ) : (
                  codes.map(c => (
                    <div
                      key={c.id}
                      className={cn('border rounded-lg p-3 flex items-center gap-3', c.active ? 'bg-white border-stone-200' : 'bg-stone-50 border-stone-200 opacity-70')}
                      data-testid={`code-row-${c.code}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold tracking-wider text-stone-900">{c.code}</span>
                          <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full',
                            c.active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-500')}>
                            {c.active ? 'ACTIVE' : 'PAUSED'}
                          </span>
                          <span className="text-[10px] bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">
                            {TYPE_LABELS[c.type]?.label || c.type}
                          </span>
                        </div>
                        <p className="text-xs text-stone-600 mt-1">
                          {c.type === 'extended_trial' ? (
                            <><Clock size={11} className="inline mr-1" /> {c.trial_days}-day trial</>
                          ) : (
                            <><Percent size={11} className="inline mr-1" /> {c.percent_off}% off</>
                          )}
                          {c.max_uses ? <> · Used {c.usage_count || 0}/{c.max_uses}</> : <> · Used {c.usage_count || 0}</>}
                          {c.expires_at && <> · Expires {new Date(c.expires_at).toLocaleDateString()}</>}
                        </p>
                        {c.notes && <p className="text-[11px] text-stone-400 italic mt-0.5 truncate">{c.notes}</p>}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => toggleCodeActive(c)} className="text-stone-600" data-testid={`toggle-code-${c.code}`}>
                        {c.active ? <XCircle size={16} /> : <CheckCircle2 size={16} />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteCode(c)} className="text-red-500 hover:text-red-700" data-testid={`delete-code-${c.code}`}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Premium Grants */}
          <TabsContent value="grants">
            <Card>
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-base">Comp'd Premium by Phone</CardTitle>
                <Button size="sm" onClick={() => setGrantDialogOpen(true)} className="bg-amber-600 hover:bg-amber-500 text-white" data-testid="new-grant-btn">
                  <Plus size={14} className="mr-1" /> Grant Access
                </Button>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-stone-500 pb-2">
                  Phone numbers listed here get full Premium forever (or until expiry). Works whether or not the user has already registered.
                </p>
                {grants.length === 0 ? (
                  <p className="text-stone-500 text-sm text-center py-6">No grants yet.</p>
                ) : (
                  grants.map(g => {
                    const isExpired = g.expires_at && new Date(g.expires_at) < new Date();
                    return (
                      <div
                        key={g.id}
                        className={cn('border rounded-lg p-3 flex items-center gap-3',
                          isExpired ? 'bg-stone-50 border-stone-200 opacity-60' : 'bg-white border-stone-200')}
                        data-testid={`grant-row-${g.phone_number}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-sm font-semibold text-stone-900">{g.phone_number}</span>
                            {g.user?.username && (
                              <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                                {g.user.username}
                              </span>
                            )}
                            {isExpired ? (
                              <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full">EXPIRED</span>
                            ) : g.expires_at ? (
                              <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                <Calendar size={10} className="inline mr-0.5" /> until {new Date(g.expires_at).toLocaleDateString()}
                              </span>
                            ) : (
                              <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">FOREVER</span>
                            )}
                          </div>
                          {g.note && <p className="text-[11px] text-stone-500 italic mt-0.5 truncate">{g.note}</p>}
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => revokeGrant(g)} className="text-red-500 hover:text-red-700" data-testid={`revoke-grant-${g.phone_number}`}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Marketing */}
          <TabsContent value="marketing">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Megaphone size={16} className="text-amber-600" /> Marketing Flyer Generator
                </CardTitle>
                <p className="text-xs text-stone-500">
                  Generate a printable 1-page PDF flyer with QR code. Leave blank for a general flyer,
                  or enter a league name to personalize the top corner with &quot;Prepared for: [league]&quot;.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gradient-to-br from-amber-50 to-green-50 border border-amber-200 rounded-lg p-4 space-y-3">
                  <div>
                    <Label htmlFor="flyer-league-input" className="text-[11px] uppercase tracking-wider text-stone-600 font-bold">
                      League name (optional)
                    </Label>
                    <Input
                      id="flyer-league-input"
                      placeholder="e.g., West Akron Little League"
                      value={flyerLeague}
                      onChange={e => setFlyerLeague(e.target.value)}
                      maxLength={60}
                      onKeyDown={e => e.key === 'Enter' && !flyerDownloading && downloadMarketingFlyer()}
                      className="bg-white mt-1"
                      data-testid="marketing-flyer-league-input"
                    />
                    <p className="text-[10px] text-stone-500 mt-1">
                      Max 60 characters. Leave blank for the general flyer.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="flyer-promo-input" className="text-[11px] uppercase tracking-wider text-stone-600 font-bold">
                      Promo code (optional)
                    </Label>
                    <Input
                      id="flyer-promo-input"
                      placeholder="e.g., LAUNCH50"
                      value={flyerPromo}
                      onChange={e => setFlyerPromo(e.target.value.toUpperCase())}
                      maxLength={30}
                      onKeyDown={e => e.key === 'Enter' && !flyerDownloading && downloadMarketingFlyer()}
                      className="bg-white mt-1 font-mono uppercase tracking-wider"
                      data-testid="marketing-flyer-promo-input"
                    />
                    <p className="text-[10px] text-stone-500 mt-1">
                      Must be an ACTIVE code from the Discount Codes tab. Adds a bright &quot;LIMITED-TIME OFFER&quot; band to the flyer and embeds the code in the QR for auto-apply.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={downloadMarketingFlyer}
                      disabled={flyerDownloading}
                      className="flex-1 bg-green-800 hover:bg-green-700 text-white font-bold"
                      data-testid="marketing-flyer-download-btn"
                    >
                      {flyerDownloading
                        ? <><Loader2 size={14} className="mr-2 animate-spin" /> Generating...</>
                        : <><Download size={14} className="mr-2" /> Download PDF flyer</>}
                    </Button>
                    {(flyerLeague.trim() || flyerPromo.trim()) && (
                      <Button
                        variant="outline"
                        onClick={() => { setFlyerLeague(''); setFlyerPromo(''); }}
                        className="border-stone-300"
                        data-testid="marketing-flyer-clear-btn"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {/* Quick tips */}
                <div className="bg-white border border-stone-200 rounded-lg p-4 space-y-2">
                  <h4 className="text-sm font-bold text-stone-800 flex items-center gap-1.5">
                    <Printer size={14} className="text-green-700" /> Distribution ideas
                  </h4>
                  <ul className="text-xs text-stone-600 space-y-1 list-disc list-inside">
                    <li>Email league presidents — each PDF arrives pre-branded for their league</li>
                    <li>Print and pin to dugout fences, scorer&apos;s booths, concession stands</li>
                    <li>Hand out at coaches&apos; pre-season meetings</li>
                    <li>Tournament directors can attach to team welcome packets</li>
                  </ul>
                </div>

                {/* Direct links */}
                <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-[11px] text-stone-600 font-mono break-all">
                  <div className="text-[10px] uppercase text-stone-500 font-bold font-sans mb-1">Direct URLs (no login needed)</div>
                  <div>/api/marketing/flyer.pdf</div>
                  <div>/api/marketing/flyer.pdf?league=League+Name</div>
                  <div>/api/marketing/flyer.pdf?promo=LAUNCH50</div>
                  <div>/api/marketing/flyer.pdf?league=League+Name&amp;promo=LAUNCH50</div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Coach Welcome Email Drip */}
          <TabsContent value="emails">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Mail size={16} className="text-green-700" /> Coach Welcome Kit (Day 0 / 1 / 3)
                </CardTitle>
                <p className="text-xs text-stone-500">
                  New coaches automatically get a 3-email onboarding drip via Resend. Day 0 fires on signup; Day 1 &amp; Day 3 run every 6 hours in the background.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Preview + send test */}
                <div className="bg-gradient-to-br from-green-50 to-amber-50 border border-green-200 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { k: 'day0', label: 'DAY 0 · Welcome', who: 'Coach' },
                      { k: 'day1', label: 'DAY 1 · Invite', who: 'Coach' },
                      { k: 'day3', label: 'DAY 3 · Pro Tips', who: 'Coach' },
                      { k: 'game_reminder', label: 'GAME DAY · Reminder', who: 'Parent' },
                    ].map(s => (
                      <Button
                        key={s.k}
                        variant={emailStage === s.k ? 'default' : 'outline'}
                        onClick={() => { setEmailStage(s.k); setEmailPreviewHtml(''); }}
                        className={cn('text-[11px] font-bold flex-col h-auto py-2',
                          emailStage === s.k ? 'bg-green-700 hover:bg-green-600' : 'border-green-300 text-green-700')}
                        data-testid={`email-stage-${s.k}`}
                      >
                        <span>{s.label}</span>
                        <span className={cn('text-[9px] font-normal mt-0.5',
                          emailStage === s.k ? 'text-green-100' : 'text-stone-500')}>
                          to {s.who}
                        </span>
                      </Button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="your@email.com (optional - to actually send)"
                      value={emailTo}
                      onChange={e => setEmailTo(e.target.value)}
                      type="email"
                      className="bg-white flex-1"
                      data-testid="email-test-to-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => previewWelcomeEmail(false)}
                      disabled={emailSending}
                      className="flex-1 border-green-600 text-green-700"
                      data-testid="email-preview-btn"
                    >
                      {emailSending ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Eye size={14} className="mr-2" />}
                      Preview HTML
                    </Button>
                    <Button
                      onClick={() => previewWelcomeEmail(true)}
                      disabled={emailSending || !emailTo.trim()}
                      className="flex-1 bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold"
                      data-testid="email-send-test-btn"
                    >
                      {emailSending ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
                      Send test email
                    </Button>
                  </div>
                  <p className="text-[10px] text-stone-500">
                    Note: Until you verify a custom domain in Resend, test emails only reach the account owner&apos;s verified address (onboarding@resend.dev sandbox rule).
                  </p>
                </div>

                {/* Live HTML preview */}
                {emailPreviewHtml && (
                  <div className="border border-stone-200 rounded-lg overflow-hidden" data-testid="email-preview-frame-wrap">
                    <div className="bg-stone-100 px-3 py-2 border-b border-stone-200 text-xs">
                      <span className="font-bold text-stone-600">Subject:</span> <span className="text-stone-800">{emailPreviewSubject}</span>
                    </div>
                    <iframe
                      srcDoc={emailPreviewHtml}
                      title="email-preview"
                      className="w-full h-[500px] bg-white"
                      data-testid="email-preview-frame"
                    />
                  </div>
                )}

                {/* Manual drip run + results */}
                <div className="bg-white border border-stone-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-bold text-stone-800">Coach drip sweep</h4>
                      <p className="text-[11px] text-stone-500">Forces an immediate Day 1 / Day 3 sweep instead of waiting for the 6h timer.</p>
                    </div>
                    <Button
                      onClick={runDripNow}
                      disabled={dripRunning}
                      className="bg-green-800 hover:bg-green-700 text-white"
                      data-testid="run-drip-btn"
                    >
                      {dripRunning ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
                      Run drip now
                    </Button>
                  </div>
                  {dripResult && (
                    <div className="grid grid-cols-4 gap-2 text-center" data-testid="drip-result">
                      <div className="bg-green-50 rounded p-2"><div className="text-lg font-bold text-green-700">{dripResult.day1}</div><div className="text-[9px] uppercase text-stone-500">Day 1 sent</div></div>
                      <div className="bg-amber-50 rounded p-2"><div className="text-lg font-bold text-amber-700">{dripResult.day3}</div><div className="text-[9px] uppercase text-stone-500">Day 3 sent</div></div>
                      <div className="bg-stone-50 rounded p-2"><div className="text-lg font-bold text-stone-600">{dripResult.skipped}</div><div className="text-[9px] uppercase text-stone-500">Skipped</div></div>
                      <div className="bg-red-50 rounded p-2"><div className="text-lg font-bold text-red-600">{dripResult.errors}</div><div className="text-[9px] uppercase text-stone-500">Errors</div></div>
                    </div>
                  )}
                </div>

                {/* Parent Game Day Reminder sweep */}
                <div className="bg-white border border-amber-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-bold text-stone-800">Parent game-day reminders</h4>
                      <p className="text-[11px] text-stone-500">Finds games scheduled 14-26 hrs from now and emails every parent following that team. One email per (parent, game).</p>
                    </div>
                    <Button
                      onClick={runGameRemindersNow}
                      disabled={remindersRunning}
                      className="bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold"
                      data-testid="run-reminders-btn"
                    >
                      {remindersRunning ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
                      Run reminders now
                    </Button>
                  </div>
                  {remindersResult && (
                    <div className="grid grid-cols-4 gap-2 text-center" data-testid="reminders-result">
                      <div className="bg-amber-50 rounded p-2"><div className="text-lg font-bold text-amber-700">{remindersResult.sent}</div><div className="text-[9px] uppercase text-stone-500">Emails sent</div></div>
                      <div className="bg-green-50 rounded p-2"><div className="text-lg font-bold text-green-700">{remindersResult.games_checked}</div><div className="text-[9px] uppercase text-stone-500">Games checked</div></div>
                      <div className="bg-stone-50 rounded p-2"><div className="text-lg font-bold text-stone-600">{remindersResult.skipped}</div><div className="text-[9px] uppercase text-stone-500">Skipped</div></div>
                      <div className="bg-red-50 rounded p-2"><div className="text-lg font-bold text-red-600">{remindersResult.errors}</div><div className="text-[9px] uppercase text-stone-500">Errors</div></div>
                    </div>
                  )}
                </div>

                {/* AI Lineup feature announcement (one-time blast) */}
                <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 border border-amber-300 rounded-lg p-4 space-y-3" data-testid="ai-lineup-blast-card">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h4 className="text-sm font-bold text-stone-800 flex items-center gap-1.5">
                        <Sparkles size={14} className="text-amber-600" /> AI Lineup announcement
                      </h4>
                      <p className="text-[11px] text-stone-500">One-time email blast to every coach with an email on record. Idempotent — clicking again only emails new coaches who didn't get it.</p>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <Button
                        onClick={previewAiBlast}
                        disabled={aiBlastLoading}
                        variant="outline"
                        size="sm"
                        className="border-amber-400 text-amber-700 hover:bg-amber-50"
                        data-testid="ai-blast-preview-btn"
                      >
                        {aiBlastLoading ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Eye size={12} className="mr-1" />}
                        Preview count
                      </Button>
                      <Button
                        onClick={sendAiBlast}
                        disabled={aiBlastLoading}
                        className="bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold"
                        size="sm"
                        data-testid="ai-blast-send-btn"
                      >
                        {aiBlastLoading ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Send size={12} className="mr-1" />}
                        Send blast
                      </Button>
                    </div>
                  </div>
                  {aiBlastResult && (
                    <div className="grid grid-cols-4 gap-2 text-center" data-testid="ai-blast-result">
                      <div className="bg-white rounded p-2"><div className="text-lg font-bold text-stone-700">{aiBlastResult.total_coaches ?? aiBlastResult.total_coaches_with_email}</div><div className="text-[9px] uppercase text-stone-500">Total coaches</div></div>
                      <div className="bg-stone-50 rounded p-2"><div className="text-lg font-bold text-stone-600">{aiBlastResult.already_sent_before ?? aiBlastResult.already_sent}</div><div className="text-[9px] uppercase text-stone-500">Already sent</div></div>
                      <div className="bg-amber-50 rounded p-2"><div className="text-lg font-bold text-amber-700">{aiBlastResult.sent_now ?? aiBlastResult.would_send_to}</div><div className="text-[9px] uppercase text-stone-500">{aiBlastResult.sent_now != null ? 'Sent now' : 'Would send'}</div></div>
                      <div className="bg-red-50 rounded p-2"><div className="text-lg font-bold text-red-600">{aiBlastResult.failed_now ?? 0}</div><div className="text-[9px] uppercase text-stone-500">Failed</div></div>
                    </div>
                  )}
                </div>

                {/* Blog post announcement (any published article) */}
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/40 border border-emerald-300 rounded-lg p-4 space-y-3" data-testid="blog-blast-card">
                  <div>
                    <h4 className="text-sm font-bold text-stone-800 flex items-center gap-1.5">
                      <FileText size={14} className="text-emerald-600" /> Blog post announcement
                    </h4>
                    <p className="text-[11px] text-stone-500">Email coaches a "new on the blog" announcement for any published article. Idempotent per article — clicking again only emails coaches who haven't received this specific post.</p>
                  </div>
                  <div className="grid md:grid-cols-[1fr_auto] gap-2 items-end">
                    <div>
                      <Label className="text-[11px]">Article slug <span className="text-stone-400 font-normal">(from /blog/&lt;slug&gt;)</span></Label>
                      <Input
                        value={blogBlastSlug}
                        onChange={e => setBlogBlastSlug(e.target.value)}
                        placeholder="5-things-every-little-league-coach-should-track"
                        className="font-mono text-sm"
                        data-testid="blog-blast-slug-input"
                      />
                    </div>
                    <div className="flex gap-1.5">
                      <Button
                        onClick={previewBlogBlastCount}
                        disabled={blogBlastLoading}
                        variant="outline"
                        size="sm"
                        className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                        data-testid="blog-blast-count-btn"
                      >
                        {blogBlastLoading ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Eye size={12} className="mr-1" />}
                        Preview count
                      </Button>
                      <Button
                        onClick={sendBlogBlast}
                        disabled={blogBlastLoading || !blogBlastSlug.trim()}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold"
                        size="sm"
                        data-testid="blog-blast-send-btn"
                      >
                        {blogBlastLoading ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Send size={12} className="mr-1" />}
                        Send blast
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px]">Auto-apply promo <span className="text-stone-400 font-normal">(appended to article URL — readers get the discount)</span></Label>
                    <Input
                      value={blogBlastPromo}
                      onChange={e => setBlogBlastPromo(e.target.value.toUpperCase())}
                      placeholder="TRIAL30"
                      className="font-mono text-sm uppercase tracking-wider max-w-xs"
                      data-testid="blog-blast-promo-input"
                    />
                    <p className="text-[10px] text-stone-500 mt-1">Active codes only. Invalid/inactive codes are silently dropped.</p>
                  </div>
                  <div className="grid md:grid-cols-[1fr_auto_auto] gap-2 items-end">
                    <div>
                      <Label className="text-[11px]">Send test email to <span className="text-stone-400 font-normal">(optional)</span></Label>
                      <Input
                        type="email"
                        value={blogBlastTo}
                        onChange={e => setBlogBlastTo(e.target.value)}
                        placeholder="you@example.com"
                        data-testid="blog-blast-test-to"
                      />
                    </div>
                    <Button
                      onClick={() => previewBlogBlast(false)}
                      disabled={blogBlastLoading}
                      variant="outline"
                      size="sm"
                      className="border-stone-300"
                      data-testid="blog-blast-render-btn"
                    >
                      Render preview
                    </Button>
                    <Button
                      onClick={() => previewBlogBlast(true)}
                      disabled={blogBlastLoading || !blogBlastTo.trim()}
                      variant="outline"
                      size="sm"
                      className="border-stone-300"
                      data-testid="blog-blast-test-send-btn"
                    >
                      Send test
                    </Button>
                  </div>
                  {blogBlastResult && (
                    <div className="grid grid-cols-4 gap-2 text-center" data-testid="blog-blast-result">
                      <div className="bg-white rounded p-2"><div className="text-lg font-bold text-stone-700">{blogBlastResult.total_coaches ?? blogBlastResult.total_coaches_with_email}</div><div className="text-[9px] uppercase text-stone-500">Total coaches</div></div>
                      <div className="bg-stone-50 rounded p-2"><div className="text-lg font-bold text-stone-600">{blogBlastResult.already_sent_before ?? blogBlastResult.already_sent}</div><div className="text-[9px] uppercase text-stone-500">Already sent</div></div>
                      <div className="bg-emerald-50 rounded p-2"><div className="text-lg font-bold text-emerald-700">{blogBlastResult.sent_now ?? blogBlastResult.would_send_to}</div><div className="text-[9px] uppercase text-stone-500">{blogBlastResult.sent_now != null ? 'Sent now' : 'Would send'}</div></div>
                      <div className="bg-red-50 rounded p-2"><div className="text-lg font-bold text-red-600">{blogBlastResult.failed_now ?? 0}</div><div className="text-[9px] uppercase text-stone-500">Failed</div></div>
                    </div>
                  )}
                  {blogBlastPreviewHtml && (
                    <div className="bg-white border border-stone-200 rounded p-2" data-testid="blog-blast-preview">
                      <div className="text-[11px] text-stone-500 mb-1"><b>Subject:</b> {blogBlastPreviewSubject}</div>
                      <iframe
                        title="blog-blast-preview"
                        srcDoc={blogBlastPreviewHtml}
                        className="w-full h-[420px] border border-stone-200 rounded bg-stone-50"
                      />
                    </div>
                  )}
                </div>

                {/* Recent deliveries */}
                <div className="bg-white border border-stone-200 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold text-stone-800">Recent deliveries</h4>
                    <Button variant="ghost" size="sm" onClick={loadDeliveries} className="text-stone-500 text-xs" data-testid="refresh-deliveries-btn">Refresh</Button>
                  </div>
                  {deliveries.length === 0 ? (
                    <p className="text-xs text-stone-400 py-3 text-center">No deliveries yet. Register a coach with an email to trigger Day 0.</p>
                  ) : (
                    <div className="divide-y divide-stone-100 max-h-64 overflow-y-auto" data-testid="deliveries-list">
                      {deliveries.map(d => (
                        <div key={d.id} className="flex items-center justify-between py-1.5 text-xs" data-testid={`delivery-${d.id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-mono text-stone-700">{d.email}</div>
                            <div className="text-[10px] text-stone-400">{d.subject}</div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={cn('text-[9px] font-bold px-2 py-0.5 rounded-full uppercase',
                              d.stage === 'day0' ? 'bg-green-100 text-green-700' : d.stage === 'day1' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700')}>
                              {d.stage}
                            </span>
                            <span className={cn('text-[9px] font-bold px-2 py-0.5 rounded-full',
                              d.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700')}>
                              {d.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* League Partner Inquiries CRM */}
          <TabsContent value="leagues">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Trophy size={16} className="text-amber-600" /> League Partner Inquiries
                </CardTitle>
                <p className="text-xs text-stone-500">
                  Leads from the <code className="bg-stone-100 px-1 rounded text-[10px]">/league-partners</code> landing page.
                  Track each one through your outreach pipeline.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Filter chips */}
                <div className="flex flex-wrap gap-1.5" data-testid="inquiry-filter-chips">
                  {[{ value: 'all', label: 'All', pill: 'bg-stone-200 text-stone-700 border-stone-300' }, ...INQUIRY_STATUSES].map(opt => {
                    const count = opt.value === 'all'
                      ? inquiries.length
                      : inquiries.filter(x => (x.status || 'new') === opt.value).length;
                    const isActive = inquiryFilter === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => setInquiryFilter(opt.value)}
                        className={cn(
                          'text-[11px] px-2.5 py-1 rounded-full border font-semibold transition',
                          isActive ? opt.pill + ' ring-2 ring-offset-1 ring-stone-400' : 'bg-white text-stone-500 border-stone-200 hover:bg-stone-50'
                        )}
                        data-testid={`inquiry-filter-${opt.value}`}
                      >
                        {opt.label} <span className="opacity-70">({count})</span>
                      </button>
                    );
                  })}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={loadInquiries}
                    className="ml-auto text-[11px] text-stone-500 hover:text-stone-800"
                    data-testid="inquiries-refresh-btn"
                  >
                    <Loader2 size={12} className="mr-1" /> Refresh
                  </Button>
                </div>

                {/* Inquiry rows */}
                {inquiries.length === 0 ? (
                  <p className="text-stone-500 text-sm text-center py-8" data-testid="inquiries-empty">
                    No league inquiries yet. When a commissioner submits the
                    <span className="font-semibold"> /league-partners</span> form, they'll appear here.
                  </p>
                ) : (
                  <div className="space-y-2" data-testid="inquiry-list">
                    {inquiries
                      .filter(q => inquiryFilter === 'all' || (q.status || 'new') === inquiryFilter)
                      .map(q => {
                        const meta = statusMeta(q.status);
                        const isOpen = expandedInquiry === q.id;
                        const receivedFmt = q.received_at ? new Date(q.received_at).toLocaleDateString() : '';
                        return (
                          <div
                            key={q.id}
                            className="border border-stone-200 rounded-lg bg-white overflow-hidden"
                            data-testid={`inquiry-row-${q.id}`}
                          >
                            {/* Summary row */}
                            <div className="p-3 flex items-start gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-bold text-stone-900 truncate">{q.league_name || '—'}</span>
                                  <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full border', meta.pill)}>
                                    {meta.label.toUpperCase()}
                                  </span>
                                  {q.teams_estimate && (
                                    <span className="text-[10px] bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">
                                      ~{q.teams_estimate} teams
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-stone-600 mt-0.5 truncate">
                                  <span className="font-semibold text-stone-700">{q.contact_name}</span>
                                  {q.email && <> · <a href={`mailto:${q.email}`} className="text-blue-600 hover:underline">{q.email}</a></>}
                                  {q.phone && <> · <a href={`tel:${q.phone}`} className="text-stone-600">{q.phone}</a></>}
                                </p>
                                <p className="text-[10px] text-stone-400 mt-0.5">
                                  Received {receivedFmt}
                                  {q.updated_at && <> · Updated {new Date(q.updated_at).toLocaleDateString()}</>}
                                </p>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <Select
                                  value={q.status || 'new'}
                                  onValueChange={v => updateInquiry(q.id, { status: v })}
                                >
                                  <SelectTrigger className="w-[130px] h-8 text-xs" data-testid={`inquiry-status-${q.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {INQUIRY_STATUSES.map(s => (
                                      <SelectItem key={s.value} value={s.value} data-testid={`inquiry-status-option-${s.value}`}>
                                        {s.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    const next = isOpen ? null : q.id;
                                    setExpandedInquiry(next);
                                    if (next) setInquiryNotesDraft(d => ({ ...d, [q.id]: q.notes || '' }));
                                  }}
                                  className="text-stone-600 h-8 w-8 p-0"
                                  data-testid={`inquiry-expand-${q.id}`}
                                >
                                  {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                </Button>
                              </div>
                            </div>

                            {/* Expanded detail */}
                            {isOpen && (
                              <div className="border-t border-stone-200 bg-stone-50 p-3 space-y-3" data-testid={`inquiry-detail-${q.id}`}>
                                {q.message && (
                                  <div>
                                    <p className="text-[11px] uppercase tracking-wider text-stone-500 font-bold mb-1">Original Message</p>
                                    <div className="bg-white border border-stone-200 rounded p-2.5 text-sm text-stone-700 whitespace-pre-wrap" data-testid={`inquiry-message-${q.id}`}>
                                      {q.message}
                                    </div>
                                  </div>
                                )}
                                <div>
                                  <p className="text-[11px] uppercase tracking-wider text-stone-500 font-bold mb-1">Internal Notes</p>
                                  <Textarea
                                    rows={3}
                                    placeholder="Call log, next steps, objections, pilot start date…"
                                    value={inquiryNotesDraft[q.id] ?? (q.notes || '')}
                                    onChange={e => setInquiryNotesDraft(d => ({ ...d, [q.id]: e.target.value }))}
                                    className="bg-white text-sm"
                                    data-testid={`inquiry-notes-${q.id}`}
                                  />
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    className="bg-emerald-600 hover:bg-emerald-500 text-white"
                                    onClick={() => updateInquiry(q.id, { notes: inquiryNotesDraft[q.id] ?? '' })}
                                    data-testid={`inquiry-save-notes-${q.id}`}
                                  >
                                    <Save size={12} className="mr-1" /> Save Notes
                                  </Button>
                                  {q.email && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      asChild
                                      data-testid={`inquiry-email-${q.id}`}
                                    >
                                      <a
                                        href={`mailto:${q.email}?subject=${encodeURIComponent('Re: Diamond Scorer for ' + (q.league_name || 'your league'))}`}
                                      >
                                        <Mail size={12} className="mr-1" /> Email
                                      </a>
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deleteInquiry(q.id)}
                                    className="ml-auto text-red-500 hover:text-red-700"
                                    data-testid={`inquiry-delete-${q.id}`}
                                  >
                                    <Trash2 size={12} className="mr-1" /> Delete
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    {inquiries.filter(q => inquiryFilter === 'all' || (q.status || 'new') === inquiryFilter).length === 0 && (
                      <p className="text-stone-400 text-xs text-center py-4" data-testid="inquiries-filter-empty">
                        No inquiries match this filter.
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Install Funnel Analytics */}
          <TabsContent value="analytics">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp size={16} className="text-emerald-600" /> Install Funnel
                </CardTitle>
                <p className="text-xs text-stone-500">
                  Tracks who opens your share links and who actually installs the PWA.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {!analytics ? (
                  <p className="text-stone-500 text-sm text-center py-6">No analytics data yet.</p>
                ) : (
                  <>
                    {/* Top-line metrics */}
                    <div className="grid grid-cols-3 gap-2" data-testid="analytics-top-metrics">
                      <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Page Views</p>
                        <p className="text-2xl font-black text-stone-800" data-testid="metric-total-views">{analytics.total_views}</p>
                      </div>
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Installs</p>
                        <p className="text-2xl font-black text-emerald-700" data-testid="metric-total-installs">{analytics.total_installs}</p>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Conversion</p>
                        <p className="text-2xl font-black text-amber-700" data-testid="metric-conversion-rate">{analytics.conversion_rate}%</p>
                      </div>
                    </div>

                    {/* Device breakdown */}
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-stone-500 font-bold mb-2 flex items-center gap-1">
                        <Smartphone size={11} /> Installs by Device
                      </p>
                      {Object.keys(analytics.by_device || {}).length === 0 ? (
                        <p className="text-stone-400 text-xs">No installs yet.</p>
                      ) : (
                        <div className="space-y-1.5" data-testid="analytics-by-device">
                          {Object.entries(analytics.by_device).map(([device, count]) => (
                            <div key={device} className="flex items-center gap-2 text-sm">
                              <span className="w-20 capitalize text-stone-600">{device}</span>
                              <div className="flex-1 bg-stone-100 rounded-full overflow-hidden h-4">
                                <div
                                  className="bg-emerald-500 h-full"
                                  style={{ width: `${Math.min(100, (count / Math.max(1, analytics.total_installs)) * 100)}%` }}
                                />
                              </div>
                              <span className="w-8 text-right font-semibold text-stone-700">{count}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Page breakdown */}
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-stone-500 font-bold mb-2">
                        Views by Page
                      </p>
                      <div className="space-y-1.5" data-testid="analytics-by-page">
                        {Object.entries(analytics.by_page || {}).map(([page, count]) => (
                          <div key={page} className="flex items-center gap-2 text-sm">
                            <span className="w-20 capitalize text-stone-600">{page.replace('_', ' ')}</span>
                            <div className="flex-1 bg-stone-100 rounded-full overflow-hidden h-4">
                              <div
                                className="bg-stone-400 h-full"
                                style={{ width: `${Math.min(100, (count / Math.max(1, analytics.total_views)) * 100)}%` }}
                              />
                            </div>
                            <span className="w-8 text-right font-semibold text-stone-700">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Top teams by install */}
                    {analytics.top_teams?.length > 0 && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-stone-500 font-bold mb-2">
                          Top Teams by Install
                        </p>
                        <div className="space-y-1" data-testid="analytics-top-teams">
                          {analytics.top_teams.map((t, i) => (
                            <div key={t.team_id} className="flex items-center gap-2 text-sm bg-stone-50 border border-stone-200 rounded px-3 py-1.5">
                              <span className="text-stone-400 text-xs w-4">#{i + 1}</span>
                              <span className="flex-1 font-medium text-stone-700 truncate">{t.team_name}</span>
                              <span className="font-bold text-emerald-700">{t.installs} installs</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscriptions Dashboard */}
          <TabsContent value="subscriptions">
            <Card>
              <CardHeader className="flex-row items-center justify-between gap-2">
                <div className="min-w-0">
                  <CardTitle className="text-base flex items-center gap-2">
                    <DollarSign size={16} className="text-emerald-600" /> Subscriptions
                  </CardTitle>
                  <p className="text-xs text-stone-500 -mt-0.5">
                    MRR, plan mix, trial conversion, recent paying signups, pending cancellations.
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={loadSubsDashboard}
                    disabled={subsLoading}
                    data-testid="subs-refresh-btn"
                  >
                    {subsLoading ? <Loader2 size={12} className="mr-1 animate-spin" /> : <RefreshCw size={12} className="mr-1" />}
                    Refresh
                  </Button>
                  <Button
                    size="sm"
                    onClick={syncStripeSubs}
                    disabled={subsSyncing}
                    className="bg-violet-600 hover:bg-violet-500 text-white"
                    data-testid="subs-sync-stripe-btn"
                  >
                    {subsSyncing ? <Loader2 size={12} className="mr-1 animate-spin" /> : <CreditCard size={12} className="mr-1" />}
                    Sync from Stripe
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {!subsData ? (
                  <p className="text-stone-500 text-sm text-center py-8" data-testid="subs-empty">
                    {subsLoading ? 'Loading subscriptions…' : 'No subscription data yet.'}
                  </p>
                ) : (
                  <>
                    {/* Top-line metrics — MRR, ARR, Active, Trialing, Pending Cancel, Past Due */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="subs-summary-tiles">
                      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/40 border border-emerald-300 rounded-lg p-3 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">MRR</p>
                        <p className="text-2xl font-black text-emerald-700" data-testid="metric-mrr">{fmtCents(subsData.summary.mrr_cents)}</p>
                        <p className="text-[10px] text-emerald-700/70 mt-0.5">ARR ≈ {fmtCentsRound(subsData.summary.arr_cents)}</p>
                      </div>
                      <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-violet-700 font-bold">Active Subs</p>
                        <p className="text-2xl font-black text-violet-700" data-testid="metric-active-subs">{subsData.summary.active_subscribers}</p>
                        <p className="text-[10px] text-violet-700/70 mt-0.5">{subsData.summary.active_paying} paid · {subsData.summary.trialing_in_stripe} trialing</p>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Pending Cancel</p>
                        <p className="text-2xl font-black text-amber-700" data-testid="metric-pending-cancel">{subsData.summary.pending_cancellation}</p>
                        <p className="text-[10px] text-amber-700/70 mt-0.5">Riding period out</p>
                      </div>
                      <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-stone-600 font-bold">In Trial</p>
                        <p className="text-2xl font-black text-stone-700" data-testid="metric-in-trial">{subsData.summary.total_trial_users}</p>
                        <p className="text-[10px] text-stone-500 mt-0.5">{subsData.summary.comp_grants_active} comp · {subsData.summary.past_due} past due</p>
                      </div>
                    </div>

                    {/* Trial conversion strip */}
                    <div className="bg-gradient-to-r from-amber-50 via-stone-50 to-emerald-50 border border-stone-200 rounded-lg p-3" data-testid="trial-conversion-card">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-stone-700">
                          Trial → Paid Conversion · last 30 days
                        </span>
                        <span className="text-xl font-extrabold text-emerald-700" data-testid="trial-conversion-pct">
                          {subsData.trial_conversion.conversion_pct}%
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-stone-200 rounded-full overflow-hidden h-3">
                          <div
                            className="bg-emerald-500 h-full transition-all"
                            style={{ width: `${Math.min(100, subsData.trial_conversion.conversion_pct)}%` }}
                          />
                        </div>
                        <span className="text-[11px] text-stone-600 font-mono shrink-0">
                          {subsData.trial_conversion.trials_converted_30d}/{subsData.trial_conversion.trials_started_30d}
                        </span>
                      </div>
                    </div>

                    {/* Plan breakdown */}
                    <div data-testid="plan-breakdown">
                      <p className="text-[11px] uppercase tracking-wider text-stone-500 font-bold mb-2">
                        Plan mix
                      </p>
                      <div className="space-y-1.5">
                        {Object.entries(subsData.plan_breakdown).map(([planId, stats]) => {
                          const meta = subsData.plan_catalog[planId] || {};
                          const pct = subsData.summary.mrr_cents > 0
                            ? (stats.mrr_cents / subsData.summary.mrr_cents) * 100
                            : 0;
                          const planColors = {
                            premium_monthly: 'bg-violet-500',
                            premium_annual:  'bg-amber-500',
                            family_monthly:  'bg-emerald-500',
                          };
                          return (
                            <div key={planId} className="bg-white border border-stone-200 rounded p-2.5" data-testid={`plan-row-${planId}`}>
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="min-w-0">
                                  <span className="text-sm font-bold text-stone-800">{meta.name || planId}</span>
                                  <span className="text-[10px] text-stone-500 ml-1.5">{fmtCents(meta.amount_cents)} / {meta.interval}</span>
                                </div>
                                <div className="text-right shrink-0">
                                  <div className="text-sm font-extrabold text-emerald-700">{fmtCents(stats.mrr_cents)}/mo</div>
                                  <div className="text-[10px] text-stone-500">
                                    {stats.count} active
                                    {stats.trialing > 0 && <> · {stats.trialing} trial</>}
                                    {stats.pending_cancel > 0 && <> · {stats.pending_cancel} canceling</>}
                                    {stats.past_due > 0 && <> · {stats.past_due} past due</>}
                                  </div>
                                </div>
                              </div>
                              <div className="bg-stone-100 rounded-full overflow-hidden h-2">
                                <div
                                  className={cn(planColors[planId] || 'bg-stone-400', 'h-full transition-all')}
                                  style={{ width: `${Math.min(100, pct)}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* 30-day revenue sparkline */}
                    <div className="bg-gradient-to-br from-emerald-50 to-stone-50 border border-emerald-200 rounded-lg p-3" data-testid="revenue-sparkline-card">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">
                          Revenue · last 30 days
                        </span>
                        <span className="text-xl font-extrabold text-emerald-700" data-testid="revenue-total-30d">
                          {fmtCents(subsData.revenue_last_30_days.total_cents)}
                        </span>
                      </div>
                      {(() => {
                        const series = subsData.revenue_last_30_days.series || [];
                        const max = Math.max(1, ...series.map(s => s.cents));
                        const W = 200;
                        const H = 50;
                        const stepX = W / Math.max(1, series.length - 1);
                        const points = series.map((s, i) => {
                          const x = i * stepX;
                          const y = H - (s.cents / max) * (H - 4) - 2;
                          return [x, y, s];
                        });
                        const polyline = points.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
                        const areaPoly = `0,${H} ` + polyline + ` ${W},${H}`;
                        return (
                          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-12" data-testid="revenue-sparkline-svg">
                            <polygon points={areaPoly} fill="#10b981" fillOpacity="0.18" />
                            <polyline points={polyline} fill="none" stroke="#10b981" strokeWidth="0.8" />
                            {points.map(([x, y, s], i) => s.cents > 0 && (
                              <circle key={i} cx={x} cy={y} r="1.1" fill="#10b981">
                                <title>{`${s.date}: ${fmtCents(s.cents)} from ${s.count} txn(s)`}</title>
                              </circle>
                            ))}
                          </svg>
                        );
                      })()}
                      <div className="flex justify-between text-[9px] text-emerald-700/60 mt-0.5">
                        <span>{subsData.revenue_last_30_days.series[0]?.date}</span>
                        <span>{subsData.revenue_last_30_days.series[subsData.revenue_last_30_days.series.length - 1]?.date}</span>
                      </div>
                    </div>

                    {/* Pending cancellations */}
                    {subsData.pending_cancellations.length > 0 && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-amber-700 font-bold mb-2 flex items-center gap-1.5">
                          <AlertTriangle size={11} /> Pending cancellations ({subsData.pending_cancellations.length})
                        </p>
                        <div className="border border-amber-200 rounded-lg overflow-hidden" data-testid="pending-cancellations-table">
                          <table className="w-full text-[12px]">
                            <thead className="bg-amber-50 text-stone-600 uppercase text-[10px] font-semibold tracking-wider">
                              <tr>
                                <th className="px-3 py-2 text-left">User</th>
                                <th className="px-3 py-2 text-left">Plan</th>
                                <th className="px-3 py-2 text-left">Ends</th>
                                <th className="px-3 py-2 text-right">Days left</th>
                              </tr>
                            </thead>
                            <tbody>
                              {subsData.pending_cancellations.map(r => (
                                <tr key={r.id} className="border-t border-amber-100 bg-white hover:bg-amber-50/40" data-testid={`pending-cancel-${r.id}`}>
                                  <td className="px-3 py-2">
                                    <div className="font-semibold text-stone-800">{r.username || '—'}</div>
                                    {r.email && <div className="text-[10px] text-stone-500">{r.email}</div>}
                                  </td>
                                  <td className="px-3 py-2 text-stone-700">
                                    <span className="text-[10px] bg-stone-100 px-1.5 py-0.5 rounded font-mono">{r.plan_id}</span>
                                  </td>
                                  <td className="px-3 py-2 text-[11px] text-stone-600">
                                    {r.subscription_current_period_end ? new Date(r.subscription_current_period_end).toLocaleDateString() : '—'}
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    {r.days_until_cancel != null ? (
                                      <span className={cn('text-[11px] font-bold px-2 py-0.5 rounded-full',
                                        r.days_until_cancel <= 3 ? 'bg-red-100 text-red-700' :
                                        r.days_until_cancel <= 7 ? 'bg-amber-100 text-amber-700' :
                                        'bg-stone-100 text-stone-600')}>
                                        {r.days_until_cancel} {r.days_until_cancel === 1 ? 'day' : 'days'}
                                      </span>
                                    ) : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Recent paying subscribers */}
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-stone-500 font-bold mb-2">
                        Recent subscribers ({subsData.recent_subscribers.length})
                      </p>
                      {subsData.recent_subscribers.length === 0 ? (
                        <p className="text-stone-400 text-xs text-center py-6 bg-stone-50 rounded">
                          No paid subscriptions on Stripe yet. They&apos;ll appear here as they sign up.
                        </p>
                      ) : (
                        <div className="border border-stone-200 rounded-lg overflow-hidden" data-testid="recent-subscribers-table">
                          <div className="max-h-[400px] overflow-y-auto">
                            <table className="w-full text-[12px]">
                              <thead className="bg-stone-100 text-stone-600 uppercase text-[10px] font-semibold tracking-wider sticky top-0">
                                <tr>
                                  <th className="px-3 py-2 text-left">User</th>
                                  <th className="px-3 py-2 text-left">Plan</th>
                                  <th className="px-3 py-2 text-left">Status</th>
                                  <th className="px-3 py-2 text-right">MRR</th>
                                  <th className="px-3 py-2 text-left">Joined</th>
                                </tr>
                              </thead>
                              <tbody>
                                {subsData.recent_subscribers.map(r => {
                                  const statusColor =
                                    r.subscription_status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                                    r.subscription_status === 'trialing' ? 'bg-blue-100 text-blue-700' :
                                    r.subscription_status === 'past_due' ? 'bg-red-100 text-red-700' :
                                    r.subscription_status === 'canceled' ? 'bg-stone-200 text-stone-500' :
                                    'bg-stone-100 text-stone-600';
                                  return (
                                    <tr key={r.id} className="border-t border-stone-100 bg-white hover:bg-stone-50" data-testid={`subscriber-${r.id}`}>
                                      <td className="px-3 py-2">
                                        <div className="font-semibold text-stone-800">{r.username || '—'}</div>
                                        {r.email && <div className="text-[10px] text-stone-500">{r.email}</div>}
                                      </td>
                                      <td className="px-3 py-2 text-stone-700">
                                        <span className="text-[10px] bg-stone-100 px-1.5 py-0.5 rounded font-mono">{r.plan_id}</span>
                                      </td>
                                      <td className="px-3 py-2">
                                        <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full uppercase', statusColor)}>
                                          {r.subscription_status}
                                        </span>
                                        {r.cancel_at_period_end && (
                                          <span className="text-[9px] font-bold ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 uppercase">
                                            Canceling
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-right font-bold text-emerald-700">
                                        {r.mrr_cents > 0 ? fmtCents(r.mrr_cents) : '—'}
                                      </td>
                                      <td className="px-3 py-2 text-[11px] text-stone-500">
                                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Fine print */}
                    <p className="text-[10px] text-stone-400 italic text-center pt-1" data-testid="subs-footer-note">
                      MRR excludes subs flagged to cancel at period end · Annual plans normalized as /12 ·
                      Updated {subsData.fetched_at ? new Date(subsData.fetched_at).toLocaleString() : 'just now'}.
                      Click <span className="font-semibold">Sync from Stripe</span> if numbers look stale.
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users — full registry of coaches, parents, admins */}
          <TabsContent value="users">
            <Card>
              <CardHeader className="flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users size={16} className="text-violet-600" /> Registered Users
                  </CardTitle>
                  <p className="text-xs text-stone-500 -mt-0.5">
                    Everyone who&apos;s signed up. Search by name, email, or phone. Export to CSV anytime.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => loadUsers()}
                    disabled={usersLoading}
                    data-testid="users-refresh-btn"
                  >
                    {usersLoading
                      ? <Loader2 size={12} className="mr-1 animate-spin" />
                      : <Loader2 size={12} className="mr-1" />}
                    Refresh
                  </Button>
                  <Button
                    size="sm"
                    onClick={exportUsersCsv}
                    disabled={!usersData?.rows?.length}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white"
                    data-testid="users-export-csv-btn"
                  >
                    <Download size={12} className="mr-1" />
                    CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Live Activity — check before deploying! */}
                {liveActivity && (
                  <div
                    className={`rounded-lg p-3 border-2 ${
                      liveActivity.safe_to_deploy
                        ? 'bg-emerald-50 border-emerald-300'
                        : 'bg-red-50 border-red-400 animate-pulse'
                    }`}
                    data-testid="live-activity-card"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-stone-700">
                        Live activity right now
                      </span>
                      <span className={`text-[11px] font-bold ${liveActivity.safe_to_deploy ? 'text-emerald-700' : 'text-red-700'}`} data-testid="live-activity-verdict">
                        {liveActivity.verdict}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center mb-2">
                      <div className="bg-white rounded p-2">
                        <div className="text-lg font-bold text-stone-800">{liveActivity.last_5_min?.plays_logged ?? 0}</div>
                        <div className="text-[9px] uppercase text-stone-500">Plays · last 5 min</div>
                      </div>
                      <div className="bg-white rounded p-2">
                        <div className="text-lg font-bold text-stone-800">{liveActivity.last_30_min?.plays_logged ?? 0}</div>
                        <div className="text-[9px] uppercase text-stone-500">Plays · last 30 min</div>
                      </div>
                      <div className="bg-white rounded p-2">
                        <div className="text-lg font-bold text-stone-800">{liveActivity.last_30_min?.distinct_active_coaches ?? 0}</div>
                        <div className="text-[9px] uppercase text-stone-500">Active coaches · 30m</div>
                      </div>
                    </div>
                    {liveActivity.live_games && liveActivity.live_games.length > 0 && (
                      <div className="bg-white rounded p-2 mt-2">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-stone-600 mb-1">Live games:</div>
                        <ul className="space-y-0.5">
                          {liveActivity.live_games.map((g) => (
                            <li key={g.id} className="text-[11px] text-stone-700 flex items-center gap-2" data-testid={`live-game-${g.id}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${g.streaming ? 'bg-red-500 animate-pulse' : 'bg-amber-500'}`} />
                              vs {g.opponent || '?'}
                              {g.streaming && <span className="text-[9px] uppercase text-red-600 font-bold">streaming</span>}
                              {g.in_lightning_delay && <span className="text-[9px] uppercase text-amber-600 font-bold">⚡ delay</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <button
                      onClick={loadLiveActivity}
                      className="text-[10px] text-stone-500 hover:text-stone-700 underline-offset-2 hover:underline mt-1"
                      data-testid="live-activity-refresh-btn"
                    >
                      ↻ Refresh check
                    </button>
                  </div>
                )}

                {/* Sparkline — last 14 days of new signups */}
                {signupsSeries && (
                  <div className="bg-gradient-to-r from-violet-50 to-stone-50 border border-violet-200 rounded-lg p-3" data-testid="users-sparkline">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-violet-700">
                        New signups · last 14 days
                      </span>
                      <span className="text-lg font-extrabold text-violet-700">{signupsSeries.total_in_window}</span>
                    </div>
                    {(() => {
                      const max = Math.max(1, ...signupsSeries.series.map(s => s.count));
                      const W = 100;
                      const H = 36;
                      const stepX = W / Math.max(1, signupsSeries.series.length - 1);
                      const points = signupsSeries.series.map((s, i) => {
                        const x = i * stepX;
                        const y = H - (s.count / max) * (H - 4) - 2;
                        return [x, y, s];
                      });
                      const polyline = points.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
                      const areaPoly = `0,${H} ` + polyline + ` ${W},${H}`;
                      return (
                        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-9" data-testid="users-sparkline-svg">
                          <polygon points={areaPoly} fill="#7c3aed" fillOpacity="0.15" />
                          <polyline points={polyline} fill="none" stroke="#7c3aed" strokeWidth="0.6" />
                          {points.map(([x, y, s], i) => s.count > 0 && (
                            <circle key={i} cx={x} cy={y} r="0.9" fill="#7c3aed">
                              <title>{`${s.date}: ${s.count} new`}</title>
                            </circle>
                          ))}
                        </svg>
                      );
                    })()}
                    <div className="flex justify-between text-[9px] text-violet-700/60 mt-0.5">
                      <span>{signupsSeries.series[0]?.date}</span>
                      <span>{signupsSeries.series[signupsSeries.series.length - 1]?.date}</span>
                    </div>
                  </div>
                )}

                {/* Source attribution — where signups came from */}
                {sourcesBreakdown && sourcesBreakdown.total_users > 0 && (
                  <div className="bg-gradient-to-r from-amber-50 to-stone-50 border border-amber-200 rounded-lg p-3" data-testid="sources-breakdown-card">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700">
                        Signup sources · {sourcesBreakdown.total_users} total users
                      </span>
                      <span className="text-[9px] text-amber-700/60">last 30d in parens</span>
                    </div>
                    <div className="space-y-1.5">
                      {sourcesBreakdown.buckets.map((b) => {
                        const colors = {
                          facebook: 'bg-blue-600',
                          instagram: 'bg-pink-500',
                          tiktok: 'bg-stone-900',
                          blog: 'bg-emerald-600',
                          referral: 'bg-purple-600',
                          google: 'bg-red-500',
                          email: 'bg-amber-500',
                          organic: 'bg-stone-500',
                          other: 'bg-stone-400',
                        };
                        const bar = colors[b.source] || 'bg-stone-400';
                        return (
                          <div key={b.source} className="flex items-center gap-2" data-testid={`source-row-${b.source}`}>
                            <span className="text-[11px] capitalize font-semibold text-stone-700 w-20 shrink-0">{b.source}</span>
                            <div className="flex-1 bg-stone-200 rounded-full h-3 overflow-hidden relative">
                              <div className={`${bar} h-full transition-all`} style={{ width: `${b.share_pct}%` }} />
                            </div>
                            <span className="text-[11px] text-stone-700 font-mono w-16 text-right">
                              {b.total}
                              {b.last_30d > 0 && <span className="text-stone-400"> ({b.last_30d})</span>}
                            </span>
                            <span className="text-[10px] text-stone-500 w-14 text-right" title="% who became paying subscribers">
                              {b.paying > 0 ? `${b.conversion_pct}%` : '—'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[9px] text-stone-500 mt-2">
                      Right column = % converted to paying subscribers. Boost what converts.
                    </p>
                  </div>
                )}

                {/* Live-stream analytics */}
                {streamAnalytics && (
                  <div className="bg-gradient-to-r from-red-50 to-stone-50 border border-red-200 rounded-lg p-3" data-testid="stream-analytics-card">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-red-700 flex items-center gap-1.5">
                        <Megaphone size={11} /> Live Streaming · last 7 days
                      </span>
                      {streamAnalytics.active_now > 0 && (
                        <span className="text-[10px] font-bold bg-red-600 text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                          {streamAnalytics.active_now} LIVE NOW
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="bg-white rounded-lg border border-stone-200 px-2 py-2 text-center" data-testid="stream-stat-streams">
                        <div className="text-xl font-extrabold text-red-700 leading-none">{streamAnalytics.total_streams_started}</div>
                        <div className="text-[9px] uppercase font-semibold text-stone-500 mt-1">Broadcasts</div>
                      </div>
                      <div className="bg-white rounded-lg border border-stone-200 px-2 py-2 text-center" data-testid="stream-stat-views">
                        <div className="text-xl font-extrabold text-blue-700 leading-none">{streamAnalytics.total_views}</div>
                        <div className="text-[9px] uppercase font-semibold text-stone-500 mt-1">Total Views</div>
                      </div>
                      <div className="bg-white rounded-lg border border-stone-200 px-2 py-2 text-center" data-testid="stream-stat-clicks">
                        <div className="text-xl font-extrabold text-emerald-700 leading-none">{streamAnalytics.click_count}</div>
                        <div className="text-[9px] uppercase font-semibold text-stone-500 mt-1">Watch Clicks</div>
                      </div>
                    </div>
                    {/* Mini 7-day sparkline */}
                    {streamAnalytics.daily_views_last7?.length > 0 && (() => {
                      const series = streamAnalytics.daily_views_last7;
                      const max = Math.max(1, ...series.map(s => s.count));
                      const W = 100;
                      const H = 22;
                      const stepX = W / Math.max(1, series.length - 1);
                      const points = series.map((s, i) => [i * stepX, H - (s.count / max) * (H - 2) - 1]);
                      const polyline = points.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
                      const areaPoly = `0,${H} ` + polyline + ` ${W},${H}`;
                      return (
                        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-6">
                          <polygon points={areaPoly} fill="#dc2626" fillOpacity="0.15" />
                          <polyline points={polyline} fill="none" stroke="#dc2626" strokeWidth="0.6" />
                        </svg>
                      );
                    })()}
                    {streamAnalytics.top_games?.length > 0 && (
                      <div className="mt-2 border-t border-red-200 pt-2">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-1">Top watched games</p>
                        <div className="space-y-0.5">
                          {streamAnalytics.top_games.slice(0, 5).map(g => (
                            <div key={g.game_id} className="flex items-center justify-between text-[11px]" data-testid={`stream-top-${g.game_id}`}>
                              <span className="truncate text-stone-700">
                                {g.team_name || 'Team'} <span className="text-stone-400">vs</span> {g.opponent_name || '—'}
                                {g.is_active && <span className="ml-1 text-red-600 font-bold">● LIVE</span>}
                              </span>
                              <span className="text-stone-700 font-bold tabular-nums">{g.view_count} <span className="text-stone-400 font-normal">views</span></span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Summary tiles */}
                {usersData?.summary && (
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="users-summary">
                    {[
                      { label: 'Total',         value: usersData.summary.total,         color: 'text-stone-700 bg-white' },
                      { label: 'Coaches',       value: usersData.summary.coaches,       color: 'text-amber-700 bg-amber-50' },
                      { label: 'Parents',       value: usersData.summary.parents,       color: 'text-emerald-700 bg-emerald-50' },
                      { label: 'Active Trials', value: usersData.summary.active_trials, color: 'text-blue-700 bg-blue-50' },
                      { label: 'Paid',          value: usersData.summary.paid,          color: 'text-violet-700 bg-violet-50' },
                      { label: 'Admins',        value: usersData.summary.admins,        color: 'text-rose-700 bg-rose-50' },
                    ].map(tile => (
                      <div key={tile.label} className={cn('rounded-lg border border-stone-200 px-2 py-2.5 text-center', tile.color)}>
                        <div className="text-2xl font-extrabold leading-none">{tile.value}</div>
                        <div className="text-[10px] uppercase tracking-wider mt-1 font-semibold opacity-80">{tile.label}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Filter row */}
                <div className="flex flex-col md:flex-row gap-2">
                  <div className="flex-1">
                    <Input
                      placeholder="Search by name, email, or phone…"
                      value={userSearch}
                      onChange={e => setUserSearch(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') loadUsers({ q: userSearch }); }}
                      data-testid="users-search-input"
                    />
                  </div>
                  <div className="flex gap-1.5 overflow-x-auto" data-testid="users-role-filters">
                    {[
                      { value: 'all',    label: 'All' },
                      { value: 'coach',  label: 'Coaches' },
                      { value: 'parent', label: 'Parents' },
                      { value: 'admin',  label: 'Admins' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => { setUserRoleFilter(opt.value); loadUsers({ role: opt.value }); }}
                        className={cn(
                          'text-[11px] px-2.5 py-1 rounded-full border font-semibold whitespace-nowrap transition',
                          userRoleFilter === opt.value
                            ? 'bg-violet-600 text-white border-violet-700'
                            : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'
                        )}
                        data-testid={`users-role-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Results table */}
                {usersData?.rows && usersData.rows.length > 0 ? (
                  <div className="border border-stone-200 rounded-lg overflow-hidden" data-testid="users-table">
                    <div className="max-h-[480px] overflow-y-auto">
                      <table className="w-full text-[12px]">
                        <thead className="bg-stone-100 text-stone-600 uppercase text-[10px] font-semibold tracking-wider sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left">Name</th>
                            <th className="px-3 py-2 text-left">Email</th>
                            <th className="px-3 py-2 text-left">Phone</th>
                            <th className="px-3 py-2 text-left">Role</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-center" title="Games scored">Games</th>
                            <th className="px-3 py-2 text-left">Source</th>
                            <th className="px-3 py-2 text-left">Joined</th>
                            <th className="px-2 py-2 text-right" />
                          </tr>
                        </thead>
                        <tbody>
                          {usersData.rows.map(u => {
                            const roleLabel = u.is_admin ? 'Admin' : (u.registered_as === 'parent' ? 'Parent' : 'Coach');
                            const status = u.subscription_active
                              ? { text: 'Paid', color: 'bg-violet-100 text-violet-700' }
                              : u.comp_premium_active
                                ? { text: 'Comp', color: 'bg-pink-100 text-pink-700' }
                                : u.trial_started_at
                                  ? { text: 'Trial', color: 'bg-blue-100 text-blue-700' }
                                  : { text: 'Free',  color: 'bg-stone-100 text-stone-600' };
                            const joined = u.created_at ? new Date(u.created_at).toLocaleDateString() : '—';
                            // Source badge — referral / league_inquiry / direct / null
                            let sourceBadge = null;
                            const src = u.signup_source || '';
                            if (src.startsWith('referral:')) {
                              sourceBadge = { text: src.replace('referral:', ''), color: 'bg-emerald-100 text-emerald-700', title: 'Used a referral / promo code' };
                            } else if (src.startsWith('league_inquiry:')) {
                              sourceBadge = { text: 'LEAGUE', color: 'bg-amber-100 text-amber-700', title: 'Came from a league inquiry' };
                            } else if (src.startsWith('coach_invite:')) {
                              sourceBadge = { text: 'INVITE', color: 'bg-blue-100 text-blue-700', title: 'Invited by a coach' };
                            } else if (src === 'direct') {
                              sourceBadge = { text: 'Direct', color: 'bg-stone-100 text-stone-500', title: 'Found us directly' };
                            }
                            return (
                              <tr key={u.id} className="border-t border-stone-100 hover:bg-stone-50 transition" data-testid={`users-row-${u.id}`}>
                                <td className="px-3 py-2 font-semibold text-stone-800 whitespace-nowrap">{u.username}</td>
                                <td className="px-3 py-2 text-stone-600">
                                  {u.email
                                    ? <a href={`mailto:${u.email}`} className="text-blue-600 hover:underline">{u.email}</a>
                                    : <span className="text-stone-300">—</span>}
                                </td>
                                <td className="px-3 py-2 text-stone-600 whitespace-nowrap">
                                  {u.phone_number
                                    ? <a href={`tel:${u.phone_number}`} className="hover:underline">{u.phone_number}</a>
                                    : <span className="text-stone-300">—</span>}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <span className={cn(
                                    'text-[10px] font-bold px-2 py-0.5 rounded-full',
                                    u.is_admin ? 'bg-rose-100 text-rose-700' :
                                    u.registered_as === 'parent' ? 'bg-emerald-100 text-emerald-700' :
                                    'bg-amber-100 text-amber-700'
                                  )}>{roleLabel.toUpperCase()}</span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full', status.color)}>{status.text.toUpperCase()}</span>
                                </td>
                                <td className="px-3 py-2 text-center text-stone-700 font-semibold tabular-nums">
                                  {u.games_scored != null ? u.games_scored : '—'}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {sourceBadge
                                    ? <span title={sourceBadge.title}
                                            className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full', sourceBadge.color)}>{sourceBadge.text}</span>
                                    : <span className="text-stone-300">—</span>}
                                </td>
                                <td className="px-3 py-2 text-stone-500 text-[11px] whitespace-nowrap">{joined}</td>
                                <td className="px-2 py-2 text-right relative">
                                  <button
                                    type="button"
                                    onClick={() => setActionMenuFor(actionMenuFor === u.id ? null : u.id)}
                                    className="text-stone-500 hover:text-stone-800 px-1.5 py-0.5 rounded hover:bg-stone-200"
                                    data-testid={`users-action-${u.id}`}
                                    aria-label="User actions"
                                  >
                                    ⋮
                                  </button>
                                  {actionMenuFor === u.id && (
                                    <div className="absolute right-2 top-7 z-50 bg-white border border-stone-200 rounded-lg shadow-lg py-1 min-w-[170px] text-left" data-testid={`users-menu-${u.id}`}>
                                      <button onClick={() => grantPremium(u, 30)} className="w-full px-3 py-1.5 text-[12px] hover:bg-violet-50 text-violet-700 font-semibold flex items-center gap-1.5">
                                        <Crown size={12} /> Grant 30d Premium
                                      </button>
                                      {(u.comp_premium_active || u.subscription_tier) && (
                                        <button onClick={() => revokePremium(u)} className="w-full px-3 py-1.5 text-[12px] hover:bg-stone-50 text-stone-700 flex items-center gap-1.5">
                                          <XCircle size={12} /> Revoke Comp Premium
                                        </button>
                                      )}
                                      {!u.is_admin && (
                                        <button onClick={() => promoteToAdmin(u)} className="w-full px-3 py-1.5 text-[12px] hover:bg-rose-50 text-rose-700 flex items-center gap-1.5">
                                          <Shield size={12} /> Promote to Admin
                                        </button>
                                      )}
                                      <button
                                        onClick={() => openMessageDialog(u)}
                                        disabled={!u.email}
                                        className="w-full px-3 py-1.5 text-[12px] hover:bg-blue-50 text-blue-700 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                        title={u.email ? 'Send a personal email' : 'No email on file'}
                                      >
                                        <Mail size={12} /> Send Email
                                      </button>
                                      <hr className="my-1 border-stone-100" />
                                      <button onClick={() => deleteUser(u)} className="w-full px-3 py-1.5 text-[12px] hover:bg-red-50 text-red-600 flex items-center gap-1.5">
                                        <Trash2 size={12} /> Delete User
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="bg-stone-50 px-3 py-1.5 text-[10px] text-stone-500 border-t border-stone-200 flex items-center justify-between">
                      <span>{usersData.row_count} {usersData.row_count === 1 ? 'user' : 'users'} {usersData.filtered ? 'matching filter' : 'shown'} · Limit {usersData.limit}</span>
                      {usersData.row_count >= usersData.limit && (
                        <span className="text-amber-700 font-semibold">Reached limit — refine search to see more.</span>
                      )}
                    </div>
                  </div>
                ) : usersLoading ? (
                  <p className="text-stone-500 text-sm text-center py-6"><Loader2 size={14} className="inline animate-spin mr-2" /> Loading users…</p>
                ) : (
                  <p className="text-stone-500 text-sm text-center py-6">
                    {usersData ? 'No users matched your filter.' : 'Click Refresh to load users.'}
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Site / CMS */}
          <TabsContent value="cms">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText size={16} className="text-amber-600" /> Site Content Management
                </CardTitle>
                <p className="text-xs text-stone-500">Manage public site settings, blog articles, testimonials, and contact form messages.</p>
              </CardHeader>
              <CardContent>
                {/* Sub-tabs */}
                <div className="flex flex-wrap gap-2 mb-5 border-b border-stone-200 pb-3">
                  {[
                    { key: 'settings',     label: 'Site Settings',   icon: Settings,        testid: 'cms-subtab-settings' },
                    { key: 'articles',     label: `Articles (${articles.length})`,    icon: FileText,    testid: 'cms-subtab-articles' },
                    { key: 'testimonials', label: `Testimonials (${testimonials.length})`, icon: Quote,    testid: 'cms-subtab-testimonials' },
                    { key: 'contacts',     label: `Contact Messages (${contactMsgs.length})`, icon: MessageSquare, testid: 'cms-subtab-contacts' },
                  ].map(s => {
                    const Ic = s.icon;
                    return (
                      <button
                        key={s.key}
                        onClick={() => setCmsSubTab(s.key)}
                        data-testid={s.testid}
                        className={cn(
                          'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border',
                          cmsSubTab === s.key
                            ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                            : 'bg-stone-100 text-stone-700 border-stone-200 hover:bg-stone-200',
                        )}
                      >
                        <Ic size={12} /> {s.label}
                      </button>
                    );
                  })}
                </div>

                {/* Settings */}
                {cmsSubTab === 'settings' && (
                  <div className="space-y-3" data-testid="cms-settings-panel">
                    <p className="text-xs text-stone-500 mb-2">These fields power the Contact page and the footer.</p>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Support email</Label>
                        <Input value={siteSettings.support_email} onChange={e => setSiteSettings({ ...siteSettings, support_email: e.target.value })} placeholder="hello@diamondscorer.com" data-testid="cms-settings-email" />
                      </div>
                      <div>
                        <Label className="text-xs">Support phone</Label>
                        <Input value={siteSettings.support_phone} onChange={e => setSiteSettings({ ...siteSettings, support_phone: e.target.value })} placeholder="(555) 555-5555" data-testid="cms-settings-phone" />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs">Business address</Label>
                        <Input value={siteSettings.business_address} onChange={e => setSiteSettings({ ...siteSettings, business_address: e.target.value })} placeholder="Optional — shown on Contact page" data-testid="cms-settings-address" />
                      </div>
                      <div>
                        <Label className="text-xs">Twitter / X URL</Label>
                        <Input value={siteSettings.twitter_url} onChange={e => setSiteSettings({ ...siteSettings, twitter_url: e.target.value })} placeholder="https://x.com/yourhandle" data-testid="cms-settings-twitter" />
                      </div>
                      <div>
                        <Label className="text-xs">Facebook URL</Label>
                        <Input value={siteSettings.facebook_url} onChange={e => setSiteSettings({ ...siteSettings, facebook_url: e.target.value })} data-testid="cms-settings-facebook" />
                      </div>
                      <div>
                        <Label className="text-xs">Instagram URL</Label>
                        <Input value={siteSettings.instagram_url} onChange={e => setSiteSettings({ ...siteSettings, instagram_url: e.target.value })} data-testid="cms-settings-instagram" />
                      </div>
                      <div>
                        <Label className="text-xs">YouTube URL</Label>
                        <Input value={siteSettings.youtube_url} onChange={e => setSiteSettings({ ...siteSettings, youtube_url: e.target.value })} data-testid="cms-settings-youtube" />
                      </div>
                    </div>
                    <div className="flex justify-end pt-2">
                      <Button onClick={saveSiteSettings} disabled={savingSettings} className="bg-amber-600 hover:bg-amber-500 text-white" data-testid="cms-settings-save">
                        {savingSettings ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
                        Save settings
                      </Button>
                    </div>
                  </div>
                )}

                {/* Articles */}
                {cmsSubTab === 'articles' && (
                  <div className="space-y-3" data-testid="cms-articles-panel">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-stone-500">Blog posts at <code>/blog</code>. Drafts hidden from public.</p>
                      <Button size="sm" onClick={() => setArticleDialog({ title: '', slug: '', excerpt: '', body_markdown: '', cover_image_url: '', author: 'Diamond Scorer', published: false })} className="bg-amber-600 hover:bg-amber-500 text-white" data-testid="cms-new-article-btn">
                        <Plus size={14} className="mr-1" /> New article
                      </Button>
                    </div>
                    {articles.length === 0 ? (
                      <p className="text-stone-500 text-sm text-center py-6">No articles yet. Create your first one above.</p>
                    ) : (
                      <div className="space-y-2">
                        {articles.map(a => (
                          <div key={a.id} className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-lg p-3" data-testid={`cms-article-row-${a.id}`}>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="font-bold text-sm text-stone-800 truncate">{a.title}</span>
                                {a.published
                                  ? <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Published</span>
                                  : <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-stone-200 text-stone-600">Draft</span>}
                              </div>
                              <p className="text-[11px] text-stone-500 truncate">/blog/{a.slug} · {a.view_count || 0} views</p>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <Button size="sm" variant="outline" onClick={() => togglePublishArticle(a)} data-testid={`cms-article-toggle-${a.id}`}>
                                {a.published ? 'Unpublish' : 'Publish'}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setArticleDialog({ ...a })} data-testid={`cms-article-edit-${a.id}`}>Edit</Button>
                              <Button size="icon" variant="ghost" onClick={() => deleteArticle(a)} className="text-red-600 hover:bg-red-50" data-testid={`cms-article-delete-${a.id}`}>
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Testimonials */}
                {cmsSubTab === 'testimonials' && (
                  <div className="space-y-3" data-testid="cms-testimonials-panel">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-stone-500">Published testimonials show in the LandingPage carousel.</p>
                      <Button size="sm" onClick={() => setTestimonialDialog({ name: '', role: '', quote: '', rating: 5, photo_url: '', published: true, sort_order: 0 })} className="bg-amber-600 hover:bg-amber-500 text-white" data-testid="cms-new-testimonial-btn">
                        <Plus size={14} className="mr-1" /> New testimonial
                      </Button>
                    </div>
                    {testimonials.length === 0 ? (
                      <p className="text-stone-500 text-sm text-center py-6">No testimonials yet. Add real coach quotes for social proof.</p>
                    ) : (
                      <div className="space-y-2">
                        {testimonials.map(t => (
                          <div key={t.id} className="bg-stone-50 border border-stone-200 rounded-lg p-3" data-testid={`cms-testimonial-row-${t.id}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="font-bold text-sm text-stone-800 truncate">{t.name}</span>
                                  {t.role && <span className="text-[11px] text-stone-500 truncate">· {t.role}</span>}
                                  <span className="ml-auto flex items-center gap-0.5">
                                    {[1,2,3,4,5].map(n => <span key={n} className={n <= (t.rating||5) ? 'text-amber-500' : 'text-stone-300'}>★</span>)}
                                  </span>
                                  {!t.published && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-stone-200 text-stone-600">Hidden</span>}
                                </div>
                                <p className="text-[12px] text-stone-700 line-clamp-2 italic">&ldquo;{t.quote}&rdquo;</p>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <Button size="sm" variant="outline" onClick={() => togglePublishTestimonial(t)} data-testid={`cms-testimonial-toggle-${t.id}`}>
                                  {t.published ? 'Hide' : 'Show'}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setTestimonialDialog({ ...t })} data-testid={`cms-testimonial-edit-${t.id}`}>Edit</Button>
                                <Button size="icon" variant="ghost" onClick={() => deleteTestimonial(t)} className="text-red-600 hover:bg-red-50" data-testid={`cms-testimonial-delete-${t.id}`}>
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Contact Messages */}
                {cmsSubTab === 'contacts' && (
                  <div className="space-y-3" data-testid="cms-contacts-panel">
                    <p className="text-xs text-stone-500">Submitted via the /contact form. Also delivered to {siteSettings.support_email || 'hello@diamondscorer.com'} by email.</p>
                    {contactMsgs.length === 0 ? (
                      <p className="text-stone-500 text-sm text-center py-6">No messages yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {contactMsgs.map(m => (
                          <div key={m.id} className="bg-stone-50 border border-stone-200 rounded-lg p-3" data-testid={`cms-contact-row-${m.id}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <span className="font-bold text-sm text-stone-800">{m.name}</span>
                                  <a href={`mailto:${m.email}`} className="text-[11px] text-amber-700 hover:underline">{m.email}</a>
                                  {m.delivered === false && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-800">Not delivered</span>}
                                  <span className="text-[10px] text-stone-400 ml-auto">{m.received_at ? new Date(m.received_at).toLocaleString() : ''}</span>
                                </div>
                                <p className="text-[12px] text-stone-700 whitespace-pre-wrap">{m.message}</p>
                              </div>
                              <Button size="icon" variant="ghost" onClick={() => deleteContactMsg(m)} className="text-red-600 hover:bg-red-50 shrink-0" data-testid={`cms-contact-delete-${m.id}`}>
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* New Code Dialog */}
      <Dialog open={codeDialogOpen} onOpenChange={setCodeDialogOpen}>
        <DialogContent className="max-w-md" data-testid="new-code-dialog">
          <DialogHeader>
            <DialogTitle>New Discount Code</DialogTitle>
            <DialogDescription>Create a promo code you can share or put on flyers.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Code</Label>
              <Input
                placeholder="LAUNCH50"
                value={newCode.code}
                onChange={e => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                className="uppercase tracking-wider font-mono"
                data-testid="new-code-input"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={newCode.type} onValueChange={v => setNewCode({ ...newCode, type: v })}>
                <SelectTrigger data-testid="new-code-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-stone-500 mt-1">{TYPE_LABELS[newCode.type]?.hint}</p>
            </div>
            {newCode.type === 'extended_trial' ? (
              <div>
                <Label>Trial Days</Label>
                <Input type="number" min={1} max={365} value={newCode.trial_days}
                  onChange={e => setNewCode({ ...newCode, trial_days: e.target.value })}
                  data-testid="new-code-trial-days" />
              </div>
            ) : (
              <div>
                <Label>Percent Off (1-100)</Label>
                <Input type="number" min={1} max={100} value={newCode.percent_off}
                  onChange={e => setNewCode({ ...newCode, percent_off: e.target.value })}
                  data-testid="new-code-percent" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Max Uses <span className="text-stone-400 text-xs font-normal">(optional)</span></Label>
                <Input type="number" min={1} placeholder="Unlimited" value={newCode.max_uses}
                  onChange={e => setNewCode({ ...newCode, max_uses: e.target.value })}
                  data-testid="new-code-max-uses" />
              </div>
              <div>
                <Label>Expires <span className="text-stone-400 text-xs font-normal">(optional)</span></Label>
                <Input type="date" value={newCode.expires_at}
                  onChange={e => setNewCode({ ...newCode, expires_at: e.target.value })}
                  data-testid="new-code-expires" />
              </div>
            </div>
            <div>
              <Label>Notes <span className="text-stone-400 text-xs font-normal">(internal)</span></Label>
              <Input placeholder="Launch promo on Instagram" value={newCode.notes}
                onChange={e => setNewCode({ ...newCode, notes: e.target.value })}
                data-testid="new-code-notes" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setCodeDialogOpen(false)}>Cancel</Button>
              <Button className="flex-1 bg-amber-600 hover:bg-amber-500 text-white"
                onClick={submitCode}
                disabled={!newCode.code.trim()}
                data-testid="new-code-submit">
                Create Code
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Grant Dialog */}
      <Dialog open={grantDialogOpen} onOpenChange={setGrantDialogOpen}>
        <DialogContent className="max-w-md" data-testid="new-grant-dialog">
          <DialogHeader>
            <DialogTitle>Grant Premium Access</DialogTitle>
            <DialogDescription>
              Unlock full Premium for the owner of this phone number.
              Takes effect immediately if they've registered — otherwise applies the moment they sign up.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Phone Number</Label>
              <Input
                placeholder="5551234567 or (555) 123-4567"
                value={newGrant.phone_number}
                onChange={e => setNewGrant({ ...newGrant, phone_number: e.target.value })}
                data-testid="new-grant-phone"
              />
            </div>
            <div>
              <Label>Expires <span className="text-stone-400 text-xs font-normal">(leave blank for forever)</span></Label>
              <Input type="date" value={newGrant.expires_at}
                onChange={e => setNewGrant({ ...newGrant, expires_at: e.target.value })}
                data-testid="new-grant-expires" />
            </div>
            <div>
              <Label>Note <span className="text-stone-400 text-xs font-normal">(internal)</span></Label>
              <Input placeholder="Beta tester / Friend / Give-away winner" value={newGrant.note}
                onChange={e => setNewGrant({ ...newGrant, note: e.target.value })}
                data-testid="new-grant-note" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setGrantDialogOpen(false)}>Cancel</Button>
              <Button className="flex-1 bg-amber-600 hover:bg-amber-500 text-white"
                onClick={submitGrant}
                disabled={!newGrant.phone_number.trim()}
                data-testid="new-grant-submit">
                Grant Access
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Per-user direct-message dialog (admin → user, via Resend) */}
      <Dialog open={!!messageDialog} onOpenChange={(open) => !open && setMessageDialog(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="user-message-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-stone-800">
              <Mail size={18} className="text-blue-600" />
              Email {messageDialog?.user?.username}
            </DialogTitle>
            <DialogDescription className="text-xs text-stone-500">
              Sends a branded email to <span className="font-mono">{messageDialog?.user?.email}</span> via Resend. They&apos;ll see it from your sender address.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs text-stone-600">Subject</Label>
              <Input
                placeholder="e.g. Quick note about your team..."
                value={messageDialog?.subject || ''}
                onChange={e => setMessageDialog({ ...messageDialog, subject: e.target.value })}
                maxLength={120}
                data-testid="user-message-subject"
              />
            </div>
            <div>
              <Label className="text-xs text-stone-600">Message</Label>
              <Textarea
                rows={6}
                placeholder="Type the message... line breaks are preserved. HTML is escaped."
                value={messageDialog?.body || ''}
                onChange={e => setMessageDialog({ ...messageDialog, body: e.target.value })}
                maxLength={5000}
                data-testid="user-message-body"
              />
              <div className="text-[10px] text-stone-400 text-right mt-0.5">
                {(messageDialog?.body || '').length} / 5000
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                variant="outline"
                onClick={() => setMessageDialog(null)}
                className="flex-1"
                data-testid="user-message-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={sendMessage}
                disabled={!messageDialog?.subject?.trim() || !messageDialog?.body?.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white"
                data-testid="user-message-send"
              >
                <Send size={12} className="mr-1" /> Send
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Article editor */}
      <Dialog open={!!articleDialog} onOpenChange={(o) => !o && setArticleDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cms-article-dialog">
          <DialogHeader>
            <DialogTitle>{articleDialog?.id ? 'Edit Article' : 'New Article'}</DialogTitle>
            <DialogDescription>
              Markdown supported. <code>**bold**</code> <code>*italic*</code> <code># heading</code> <code>- list</code> <code>[link](url)</code>
            </DialogDescription>
          </DialogHeader>
          {articleDialog && (
            <div className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Title <span className="text-red-500">*</span></Label>
                  <Input value={articleDialog.title || ''} onChange={e => setArticleDialog({ ...articleDialog, title: e.target.value })} data-testid="article-title-input" />
                </div>
                <div>
                  <Label className="text-xs">URL slug <span className="text-stone-400 font-normal">(auto if blank)</span></Label>
                  <Input value={articleDialog.slug || ''} onChange={e => setArticleDialog({ ...articleDialog, slug: e.target.value })} placeholder="how-to-score-a-double-play" data-testid="article-slug-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Cover image URL</Label>
                <Input value={articleDialog.cover_image_url || ''} onChange={e => setArticleDialog({ ...articleDialog, cover_image_url: e.target.value })} placeholder="https://..." data-testid="article-cover-input" />
              </div>
              <div>
                <Label className="text-xs">Excerpt <span className="text-stone-400 font-normal">(shown on listing + preview cards)</span></Label>
                <Textarea rows={2} value={articleDialog.excerpt || ''} onChange={e => setArticleDialog({ ...articleDialog, excerpt: e.target.value })} data-testid="article-excerpt-input" />
              </div>
              <div>
                <Label className="text-xs">Body (markdown)</Label>
                <Textarea rows={14} value={articleDialog.body_markdown || ''} onChange={e => setArticleDialog({ ...articleDialog, body_markdown: e.target.value })} className="font-mono text-sm" placeholder="## Why this matters&#10;&#10;Write your article in markdown here." data-testid="article-body-input" />
              </div>
              <div className="grid md:grid-cols-2 gap-3 items-center">
                <div>
                  <Label className="text-xs">Author</Label>
                  <Input value={articleDialog.author || ''} onChange={e => setArticleDialog({ ...articleDialog, author: e.target.value })} data-testid="article-author-input" />
                </div>
                <label className="flex items-center gap-2 mt-5 cursor-pointer" data-testid="article-published-toggle">
                  <input type="checkbox" checked={!!articleDialog.published} onChange={e => setArticleDialog({ ...articleDialog, published: e.target.checked })} className="w-4 h-4" />
                  <span className="text-sm font-semibold text-stone-700">Published (visible to public)</span>
                </label>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setArticleDialog(null)} data-testid="article-cancel-btn">Cancel</Button>
                <Button className="flex-1 bg-amber-600 hover:bg-amber-500 text-white" onClick={saveArticle} data-testid="article-save-btn">
                  <Save size={14} className="mr-1" /> {articleDialog.id ? 'Save changes' : 'Create article'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Testimonial editor */}
      <Dialog open={!!testimonialDialog} onOpenChange={(o) => !o && setTestimonialDialog(null)}>
        <DialogContent className="max-w-md" data-testid="cms-testimonial-dialog">
          <DialogHeader>
            <DialogTitle>{testimonialDialog?.id ? 'Edit Testimonial' : 'New Testimonial'}</DialogTitle>
            <DialogDescription>Use real coach quotes for landing page social proof.</DialogDescription>
          </DialogHeader>
          {testimonialDialog && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Name <span className="text-red-500">*</span></Label>
                <Input value={testimonialDialog.name || ''} onChange={e => setTestimonialDialog({ ...testimonialDialog, name: e.target.value })} data-testid="testimonial-name-input" />
              </div>
              <div>
                <Label className="text-xs">Role / Team</Label>
                <Input value={testimonialDialog.role || ''} onChange={e => setTestimonialDialog({ ...testimonialDialog, role: e.target.value })} placeholder="Coach · Eagles Little League" data-testid="testimonial-role-input" />
              </div>
              <div>
                <Label className="text-xs">Quote <span className="text-red-500">*</span></Label>
                <Textarea rows={4} value={testimonialDialog.quote || ''} onChange={e => setTestimonialDialog({ ...testimonialDialog, quote: e.target.value })} data-testid="testimonial-quote-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Rating (1-5)</Label>
                  <Input type="number" min={1} max={5} value={testimonialDialog.rating || 5} onChange={e => setTestimonialDialog({ ...testimonialDialog, rating: parseInt(e.target.value, 10) || 5 })} data-testid="testimonial-rating-input" />
                </div>
                <div>
                  <Label className="text-xs">Sort order</Label>
                  <Input type="number" value={testimonialDialog.sort_order || 0} onChange={e => setTestimonialDialog({ ...testimonialDialog, sort_order: parseInt(e.target.value, 10) || 0 })} data-testid="testimonial-sort-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Photo URL <span className="text-stone-400 font-normal">(optional)</span></Label>
                <Input value={testimonialDialog.photo_url || ''} onChange={e => setTestimonialDialog({ ...testimonialDialog, photo_url: e.target.value })} placeholder="https://..." data-testid="testimonial-photo-input" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="testimonial-published-toggle">
                <input type="checkbox" checked={!!testimonialDialog.published} onChange={e => setTestimonialDialog({ ...testimonialDialog, published: e.target.checked })} className="w-4 h-4" />
                <span className="text-sm font-semibold text-stone-700">Published (shown publicly)</span>
              </label>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setTestimonialDialog(null)} data-testid="testimonial-cancel-btn">Cancel</Button>
                <Button className="flex-1 bg-amber-600 hover:bg-amber-500 text-white" onClick={saveTestimonial} data-testid="testimonial-save-btn">
                  <Save size={14} className="mr-1" /> {testimonialDialog.id ? 'Save changes' : 'Create'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
